import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { DatePickerTR } from "@/components/ui/date-picker-tr";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { parseTurkishNumber, formatTurkishCurrency } from "@/utils/numberUtils";
import {
  Users,
  Plus,
  Search,
  Phone,
  Mail,
  Sparkles,
  Loader2,
  Calendar,
  CreditCard,
  ChevronRight,
  Activity,
  Package,
  Trash2,
  LayoutGrid,
  List,
  Edit,
  FileText,
  TrendingUp,
  CheckCircle2,
  AlertCircle,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  User,
  Star,
  CalendarDays,
  MessageCircle,
  Clock,
} from "lucide-react";
import { API } from "@/App";
import { format } from "date-fns";
import { tr } from "date-fns/locale";
import { getDateLocale, toLocalDateString, todayLocalString } from "@/utils/dateUtils";
import { useLocalizedData } from "@/contexts/LocalizedDataContext";
import { useCurrency } from "@/contexts/CurrencyContext";
import { useTranslation } from "react-i18next";
import { TransferPackageFields } from "@/components/TransferPackageFields";
import { MemberAlerts, MemberFlags } from "@/components/MemberAlerts";
import { activeRestriction, RestrictionPanel, RecurringTemplateInfo } from "@/components/MemberRestriction";
import { DateInputTR } from "@/components/ui/date-picker-tr";
import { LinkPendingPanel } from "@/components/LinkPendingPanel";

function TimePickerPopover({ value = "12:00", onChange }) {
  const [open, setOpen] = useState(false);
  const [selectedHour, setSelectedHour] = useState(() => value?.split(":")?.[0] || "12");
  const [selectedMinute, setSelectedMinute] = useState(() => value?.split(":")?.[1] || "00");

  const hours = Array.from({ length: 24 }).map((_, i) => String(i).padStart(2, "0"));
  const minutes = ["00", "05", "10", "15", "20", "25", "30", "35", "40", "45", "50", "55"];

  const handleConfirm = () => {
    const finalTime = `${selectedHour}:${selectedMinute}`;
    onChange(finalTime);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className="w-full justify-start text-left font-normal rounded-xl h-10 border-stone-200"
        >
          <Clock className="w-4 h-4 mr-2 text-stone-500" />
          <span className="text-sm font-semibold text-navy">{value || "Saat Seçin"}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        side="bottom"
        sideOffset={6}
        align="start"
        className="w-72 p-3 rounded-2xl shadow-2xl bg-white border border-stone-200 z-[100]"
      >
        <div className="flex items-center justify-between pb-2 border-b">
          <span className="text-base font-bold text-navy">{selectedHour}:{selectedMinute}</span>
          <Button
            size="sm"
            onClick={handleConfirm}
            className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs h-7 px-3 rounded-lg font-semibold"
          >
            Tamam
          </Button>
        </div>

        <div className="mt-2.5">
          <p className="text-[10px] font-bold text-stone-400 mb-1 tracking-wider">SAAT</p>
          <div className="grid grid-cols-8 gap-1">
            {hours.map((h) => (
              <button
                key={h}
                type="button"
                onClick={() => setSelectedHour(h)}
                className={`py-1.5 text-xs font-semibold rounded-lg transition-colors ${
                  selectedHour === h ? "bg-emerald-600 text-white" : "hover:bg-slate-100 text-stone-700"
                }`}
              >
                {h}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-2.5 pt-2 border-t">
          <p className="text-[10px] font-bold text-stone-400 mb-1 tracking-wider">DAKİKA</p>
          <div className="grid grid-cols-6 gap-1">
            {minutes.map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => setSelectedMinute(m)}
                className={`py-1.5 text-xs font-semibold rounded-lg transition-colors ${
                  selectedMinute === m ? "bg-emerald-600 text-white" : "hover:bg-slate-100 text-stone-700"
                }`}
              >
                {m}
              </button>
            ))}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default function MembersModule({ businessId, subscription, user, businessOwnerId }) {
  const [demoRequests, setDemoRequests] = useState([]);
  const [showDemoModal, setShowDemoModal] = useState(false);

  const STATUS_TR = {
    pending: "Bekliyor",
    in_progress: "Aranıyor",
    called: "Görüşüldü",
    unreachable: "Ulaşılamadı",
    demo_scheduled: "Demo Planlandı",
    converted: "Paket Satıldı",
    cancelled: "İptal Edildi"
  };

  const loadDemoRequests = async () => {
    if (!businessId) return;
    try {
      const res = await fetch(`${API}/reservations/demo-requests/${businessId}`, {
        credentials: "include",
      });
      if (res.ok) {
        const data = await res.json();
        setDemoRequests(data);
      }
    } catch (err) {
      console.error("Demo talepleri çekilemedi:", err);
    }
  };

  const handleUpdateDemoStatus = async (demoId, newStatus) => {
    try {
      const res = await fetch(`${API}/demo-requests/${demoId}/status`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ status: newStatus }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "İşlem başarısız");
      }

      toast.success(newStatus === "called" ? "Talep 'Arandı' olarak güncellendi." : "Talep iptal edildi.");
      
      setDemoRequests((prev) =>
        prev.map((r) => (r.demo_id === demoId ? { ...r, status: newStatus } : r))
      );
      loadDemoRequests();
      window.dispatchEvent(new Event("demoStatusUpdated"));
    } catch (err) {
      toast.error(err.message || "Güncelleme sırasında hata oluştu.");
    }
  };

  useEffect(() => {
    loadDemoRequests();
  }, [businessId]);

  const navigate = useNavigate();
  const { t } = useTranslation(["common", "dashboard", "members", "trainers", "reservations", "settings", "payments", "rooms", "messaging", "business", "memberDashboard", "packages", "admin", "employees", "surveys", "reports", "evaluations"]);
  const { formatPrice, currencySymbol } = useCurrency();
  const { getLocalizedBranch, getLocalizedTrainingType, getLocalizedMeasurement } = useLocalizedData();

  const [members, setMembers] = useState([]);
  const [packages, setPackages] = useState([]);
  const [memberCards, setMemberCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [showSellPackageModal, setShowSellPackageModal] = useState(false);
  const [editingDatesPkg, setEditingDatesPkg] = useState(null);
  const [savingDates, setSavingDates] = useState(false);
  const [showMemberInfoModal, setShowMemberInfoModal] = useState(false);
  const [memberFullInfo, setMemberFullInfo] = useState(null);
  
  const canEdit = subscription?.is_active && subscription?.plan !== 'free';
  
  const handleMemberClick = (member) => {
    navigate(`/dashboard/members/${member.member_id}`);
  };

  const [showMeasurementsModal, setShowMeasurementsModal] = useState(false);
  const [measurements, setMeasurements] = useState([]);
  const [loadingMeasurements, setLoadingMeasurements] = useState(false);
  const [newMeasurement, setNewMeasurement] = useState({ parameter: "", value: "" });
  const [measurementParams, setMeasurementParams] = useState([]);
  
  const measurementParameters = measurementParams;

  const [newMemberEmail, setNewMemberEmail] = useState("");
  const [selectedPackage, setSelectedPackage] = useState("none");
  const [salePrice, setSalePrice] = useState("");
  const [newMemberPaidAmount, setNewMemberPaidAmount] = useState("0");
  const [newMemberPaymentMethod, setNewMemberPaymentMethod] = useState("cash");
  const [healthNotes, setHealthNotes] = useState("");
  const [usedCard, setUsedCard] = useState("none");
  const [usedCards, setUsedCards] = useState([]);
  const [selectedTrainerForNew, setSelectedTrainerForNew] = useState("none");
  
  const [addMemberMode, setAddMemberMode] = useState("invitation");
  const [unregisteredMember, setUnregisteredMember] = useState({
    name: "",
    surname: "",
    phone: "",
    email: "",
    birth_date: "",
    gender: ""
  });
  
  const [invitationCode, setInvitationCode] = useState("");
  const [validatingCode, setValidatingCode] = useState(false);
  const [validatedUser, setValidatedUser] = useState(null);
  
  const [userSuggestions, setUserSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  
  const [viewMode, setViewMode] = useState("list");
  
  const [statusFilter, setStatusFilter] = useState("active");
  const [memberTypeFilter, setMemberTypeFilter] = useState("all");
  const [cardTypeFilter, setCardTypeFilter] = useState("all");
  const [candidatesOnly, setCandidatesOnly] = useState(false);
  const [restrictedOnly, setRestrictedOnly] = useState(false);
  const patchMember = (m) => {
    const p = { booking_restriction: m.booking_restriction, recurring_template: m.recurring_template };
    setSelectedMember((s) => (s?.member_id === m.member_id ? { ...s, ...p } : s));
    setMembers((list) => list.map((x) => (x.member_id === m.member_id ? { ...x, ...p } : x)));
  };
  const [isCandidate, setIsCandidate] = useState(false);
  const [pkgReqIds, setPkgReqIds] = useState([]);
  
  const [sortField, setSortField] = useState("name");
  const [sortDirection, setSortDirection] = useState("asc");
  
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  
  const [unregisteredBirthDateOpen, setUnregisteredBirthDateOpen] = useState(false);
  const [editBirthDateOpen, setEditBirthDateOpen] = useState(false);
  
  const [deleteMemberId, setDeleteMemberId] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteReason, setDeleteReason] = useState("");
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [statusChangeMember, setStatusChangeMember] = useState(null);
  
  const [sellPackageId, setSellPackageId] = useState("");
  const [sellPrice, setSellPrice] = useState("");
  const [sellMinPrice, setSellMinPrice] = useState(0);
  const [sellNormalPrice, setSellNormalPrice] = useState(0);
  const [sellPaidAmount, setSellPaidAmount] = useState("0");
  const [sellPaymentMethod, setSellPaymentMethod] = useState("cash");
  const [sellTrainer, setSellTrainer] = useState("");
  const [sellDiscountReason, setSellDiscountReason] = useState("");
  const [sellIsTransfer, setSellIsTransfer] = useState(false);
  const [sellTransferUsed, setSellTransferUsed] = useState("0");
  const [sellTransferEnd, setSellTransferEnd] = useState("");
  const [sellSaleDate, setSellSaleDate] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
  });
  const [trainers, setTrainers] = useState([]);
  const [activePriceList, setActivePriceList] = useState(null);
  const [currentUserTrainer, setCurrentUserTrainer] = useState(null);
  const [currentUserEmployee, setCurrentUserEmployee] = useState(null);

  useEffect(() => {
    if (!user?.user_id || user?.user_id === businessOwnerId) return;
    fetch(`${API}/employees/by-user/${user.user_id}`, { credentials: "include" })
      .then(r => (r.ok ? r.json() : []))
      .then(list => setCurrentUserEmployee((list || []).find(e => e.business_id === businessId) || null))
      .catch(() => {});
  }, [user?.user_id, businessId, businessOwnerId]);

  const isTransferManager = (s) => s?.is_manager === true && s?.permissions?.transfer_package_sale !== false;
  const canTransferPackage = (
    user?.user_id === businessOwnerId || user?.is_admin === true ||
    isTransferManager(currentUserTrainer) || isTransferManager(currentUserEmployee)
  );
  const canDeactivateMembers = (
    user?.role === "business" || user?.is_admin || user?.user_id === businessOwnerId ||
    currentUserTrainer?.is_manager === true ||
    currentUserTrainer?.permissions?.can_deactivate_members === true
  );
  
  const [memberPackagesList, setMemberPackagesList] = useState([]);
  const [loadingMemberPackages, setLoadingMemberPackages] = useState(false);
  
  const [assignTrainerPkg, setAssignTrainerPkg] = useState(null);
  const [assignTrainerId, setAssignTrainerId] = useState("none");
  const [isAssigningTrainer, setIsAssigningTrainer] = useState(false);
  
  const [memberFormStatus, setMemberFormStatus] = useState([]);
  const [loadingMemberForms, setLoadingMemberForms] = useState(false);
  
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [packageToDeactivate, setPackageToDeactivate] = useState(null);
  const [deactivateReason, setDeactivateReason] = useState("");
  
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [packageToCancel, setPackageToCancel] = useState(null);
  const [cancelType, setCancelType] = useState("free");
  const [cancelReason, setCancelReason] = useState("");
  const [refundAmount, setRefundAmount] = useState(0);
  const [refundNow, setRefundNow] = useState(true);
  const [isCancelling, setIsCancelling] = useState(false);
  
  const [isAddingMember, setIsAddingMember] = useState(false);
  const [isSellingPackage, setIsSellingPackage] = useState(false);
  const [recentSales, setRecentSales] = useState({ sales: [], stats: null });
  const [allMemberPackages, setAllMemberPackages] = useState([]);
  const [loadingRecentSales, setLoadingRecentSales] = useState(false);
  const [showRecentSalesModal, setShowRecentSalesModal] = useState(false);
  const [isSavingEdit, setIsSavingEdit] = useState(false);

  const [activeTab, setActiveTab] = useState("members");
  const [demoStatusFilter, setDemoStatusFilter] = useState("all");
  const [demoSearchQuery, setDemoSearchQuery] = useState("");
  const [demoSortAsc, setDemoSortAsc] = useState(false);
  const [selectedDemoLead, setSelectedDemoLead] = useState(null);
  const [callLogForm, setCallLogForm] = useState({ result: "no_answer", note: "" });
  const [outcomeForm, setOutcomeForm] = useState({ type: "", note: "", scheduled_date: "" });
  const [isSubmittingLead, setIsSubmittingLead] = useState(false);

  const getWhatsAppLink = (rawPhone) => {
    if (!rawPhone) return "#";
    const digits = rawPhone.replace(/\D/g, "");
    const formatted = digits.startsWith("0") ? "90" + digits.slice(1) : digits.startsWith("90") ? digits : "90" + digits;
    return `https://wa.me/${formatted}`;
  };

  useEffect(() => {
    if (businessId) {
      fetchData();
      fetchRecentSales();
    }
  }, [businessId, statusFilter]);
  
  const fetchRecentSales = async () => {
    try {
      setLoadingRecentSales(true);
      const res = await fetch(`${API}/businesses/${businessId}/recent-sales`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setRecentSales(data);
      }
    } catch (error) {
      console.error("Error fetching recent sales:", error);
    } finally {
      setLoadingRecentSales(false);
    }
  };

  const fetchData = async () => {
    try {
      let membersUrl = `${API}/businesses/${businessId}/members`;
      if (statusFilter && statusFilter !== "all") {
        membersUrl += `?status=${statusFilter}`;
      }
      
      const [
        membersRes, 
        packagesRes, 
        cardsRes, 
        trainersRes, 
        priceListRes, 
        measurementParamsRes, 
        businessRes
      ] = await Promise.all([
        fetch(membersUrl, { credentials: "include" }),
        fetch(`${API}/businesses/${businessId}/packages`, { credentials: "include" }),
        fetch(`${API}/public/member-cards`),
        fetch(`${API}/businesses/${businessId}/trainers`, { credentials: "include" }),
        fetch(`${API}/businesses/${businessId}/price-lists/active`, { credentials: "include" }),
        fetch(`${API}/businesses/${businessId}/enabled-measurement-params`, { credentials: "include" }),
        fetch(`${API}/businesses/${businessId}`, { credentials: "include" }),
      ]);

      if (membersRes.ok) setMembers(await membersRes.json());
      if (packagesRes.ok) setPackages(await packagesRes.json());

      // TÜM PAKETLERİN GELDİĞİ ASIL ENDPOINT:
      try {
        const allPkgRes = await fetch(`${API}/businesses/${businessId}/all-member-packages`, { credentials: "include" });
        if (allPkgRes.ok) {
          const data = await allPkgRes.json();
          setAllMemberPackages(data.packages || []);
        }
      } catch (e) {
        console.error("Paket listesi çekilemedi", e);
      }
      
      let acceptedCards = [];
      if (businessRes.ok) {
        const businessData = await businessRes.json();
        acceptedCards = businessData.accepted_cards || [];
      }
      
      if (cardsRes.ok) {
        const data = await cardsRes.json();
        const allCards = data.member_cards || [];
        const filteredCards = acceptedCards.length > 0 
          ? allCards.filter(card => acceptedCards.includes(card))
          : allCards;
        setMemberCards(filteredCards);
      }
      
      if (trainersRes.ok) {
        const trainersData = await trainersRes.json();
        setTrainers(trainersData);
        if (user?.user_id) {
          const userTrainer = trainersData.find(t => t.user_id === user.user_id);
          setCurrentUserTrainer(userTrainer || null);
        }
      }
      if (priceListRes.ok) {
        const priceList = await priceListRes.json();
        setActivePriceList(priceList);
      }
      if (measurementParamsRes.ok) {
        const data = await measurementParamsRes.json();
        setMeasurementParams(data.params || []);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMeasurements = async (memberId) => {
    setLoadingMeasurements(true);
    try {
      const response = await fetch(`${API}/members/${memberId}/measurements`, {
        credentials: "include",
      });
      if (response.ok) {
        setMeasurements(await response.json());
      }
    } catch (error) {
      console.error("Error fetching measurements:", error);
    } finally {
      setLoadingMeasurements(false);
    }
  };

  const handleAddMeasurement = async () => {
    if (!newMeasurement.parameter || !newMeasurement.value) {
      toast.error(t('members:paramAndValueRequired'));
      return;
    }

    const selectedParam = measurementParameters.find(p => p.name === newMeasurement.parameter);
    const unit = selectedParam?.unit || "";

    try {
      const response = await fetch(`${API}/measurements`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          member_id: selectedMember.member_id,
          parameter: newMeasurement.parameter,
          value: parseFloat(newMeasurement.value),
          unit: unit,
        }),
      });

      if (response.ok) {
        toast.success(t('members:measurementAdded'));
        setNewMeasurement({ parameter: "", value: "" });
        fetchMeasurements(selectedMember.member_id);
      } else {
        const error = await response.json();
        toast.error(error.detail || t('common:errorOccurred'));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    }
  };

  const handleDeleteMeasurement = async (measurementId) => {
    try {
      const response = await fetch(`${API}/measurements/${measurementId}`, {
        method: "DELETE",
        credentials: "include",
      });

      if (response.ok) {
        toast.success(t('members:measurementDeleted'));
        fetchMeasurements(selectedMember.member_id);
      } else {
        const error = await response.json();
        toast.error(error.detail || t('common:errorOccurred'));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    }
  };

  const openMeasurementsModal = (member) => {
    setSelectedMember(member);
    setShowMeasurementsModal(true);
    fetchMeasurements(member.member_id);
  };
  
  const searchUsers = async (query) => {
    if (query.length < 2) {
      setUserSuggestions([]);
      setSelectedUser(null);
      return;
    }
    try {
      const response = await fetch(`${API}/users/search?q=${encodeURIComponent(query)}`, {
        credentials: "include",
      });
      if (response.ok) {
        const users = await response.json();
        setUserSuggestions(users);
        setShowSuggestions(true);
      }
    } catch (error) {
      console.error("Error searching users:", error);
    }
  };
  
  const selectUser = (user) => {
    setSelectedUser(user);
    setNewMemberEmail(user.email);
    setShowSuggestions(false);
    setUserSuggestions([]);
  };
  
  const hasPackagePrice = (packageId) => {
    if (!activePriceList?.prices) return false;
    return activePriceList.prices.some(p => p.package_id === packageId);
  };
  
  const packagesWithPrice = packages
    .filter(pkg => hasPackagePrice(pkg.package_id))
    .sort((a, b) => (a.name || "").localeCompare(b.name || "", 'tr'));
  
  const openSellPackageModal = (member) => {
    setSelectedMember(member);
    setShowSellPackageModal(true);
    setSellPackageId("");
    setSellPrice("");
    setSellTrainer("");
    setSellMinPrice(0);
    setSellNormalPrice(0);
    setSellPaidAmount("0");
    setSellDiscountReason("");
  };
  
  const fetchMemberPackages = async (memberId) => {
    setLoadingMemberPackages(true);
    try {
      const response = await fetch(`${API}/members/${memberId}/packages`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setMemberPackagesList(data);
      }
    } catch (error) {
      console.error("Error fetching member packages:", error);
    } finally {
      setLoadingMemberPackages(false);
    }
  };
  
  const fetchMemberFormStatus = async (memberId) => {
    setLoadingMemberForms(true);
    try {
      const token = localStorage.getItem("auth_token");
      const response = await fetch(
        `${API}/member-forms/business/${businessId}/member/${memberId}`,
        {
          credentials: "include",
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        }
      );
      if (response.ok) {
        const data = await response.json();
        setMemberFormStatus(data);
      }
    } catch (error) {
      console.error("Error fetching member form status:", error);
    } finally {
      setLoadingMemberForms(false);
    }
  };
  
  const handleSelectMember = async (member) => {
    setSelectedMember(member);
    await Promise.all([
      fetchMemberPackages(member.member_id),
      fetchMemberFormStatus(member.member_id)
    ]);
  };
  
  const fetchMemberFullInfo = async (memberId) => {
    try {
      const response = await fetch(`${API}/members/${memberId}/full-info`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setMemberFullInfo(data);
        setShowMemberInfoModal(true);
      } else {
        toast.error(t('members:memberInfoFailed'));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    }
  };
  
  const openDeactivateModal = (pkg) => {
    setPackageToDeactivate(pkg);
    setDeactivateReason("");
    setShowDeactivateModal(true);
  };
  
  const handleDeactivatePackage = async () => {
    if (!packageToDeactivate) return;
    
    if (!deactivateReason.trim()) {
      toast.error(t('members:deactivationReasonRequired'));
      return;
    }
    
    try {
      const response = await fetch(`${API}/member-packages/${packageToDeactivate.member_package_id}/deactivate`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          reason: deactivateReason,
        }),
      });
      
      if (response.ok) {
        toast.success(t('members:packageDeactivated'));
        setShowDeactivateModal(false);
        setPackageToDeactivate(null);
        setDeactivateReason("");
        if (selectedMember) {
          fetchMemberPackages(selectedMember.member_id);
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || t("common:operationFailed"));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    }
  };
  
  const openCancelModal = (pkg) => {
    setPackageToCancel(pkg);
    setCancelType("free");
    setCancelReason("");
    setRefundAmount(pkg.paid_amount || 0);
    setRefundNow(true);
    setShowCancelModal(true);
  };
  
  const handleCancelPackage = async () => {
    if (!packageToCancel) return;
    if (isCancelling) return;
    
    if (!cancelReason.trim()) {
      toast.error(t('members:cancellationReasonRequired'));
      return;
    }
    
    if (packageToCancel.status === 'cancelled') {
      toast.error(t('members:packageAlreadyCancelled'));
      setShowCancelModal(false);
      return;
    }
    
    const isOwner = user?.user_id === selectedMember?.business_owner_id || user?.is_admin;
    const isManager = currentUserTrainer?.is_manager;
    
    if (!isOwner && !isManager) {
      toast.error(t('employees:adminPermissionRequired'));
      return;
    }
    
    setIsCancelling(true);
    
    try {
      const response = await fetch(`${API}/member-packages/${packageToCancel.member_package_id}/cancel`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          reason: cancelReason,
          cancel_type: cancelType,
          refund_amount: cancelType === "refund" ? refundAmount : 0,
          refund_now: cancelType === "refund" ? refundNow : false,
        }),
      });
      
      if (response.ok) {
        toast.success(t('members:packageCancelled'));
        setShowCancelModal(false);
        setPackageToCancel(null);
        setCancelReason("");
        setCancelType("free");
        setRefundAmount(0);
        setRefundNow(true);
        if (selectedMember) {
          fetchMemberPackages(selectedMember.member_id);
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || t("common:operationFailed"));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    } finally {
      setIsCancelling(false);
    }
  };
  
  const handleReactivatePackage = async (pkg) => {
    try {
      const response = await fetch(`${API}/member-packages/${pkg.member_package_id}/reactivate`, {
        method: "PUT",
        credentials: "include",
      });
      
      if (response.ok) {
        toast.success(t('members:packageActivated'));
        if (selectedMember) {
          fetchMemberPackages(selectedMember.member_id);
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || t("common:operationFailed"));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    }
  };

  const handleDeleteMember = (memberId, e) => {
    e?.stopPropagation();
    setDeleteMemberId(memberId);
  };
  
  const confirmDeleteMember = async () => {
    if (!deleteMemberId || isDeleting) return;
    if (!deleteReason.trim()) { toast.error("Silme nedeni zorunludur"); return; }
    setIsDeleting(true);
    
    try {
      const response = await fetch(`${API}/members/${deleteMemberId}/safe?reason=${encodeURIComponent(deleteReason.trim())}`, {
        method: "DELETE",
        credentials: "include",
      });
      
      if (response.ok) {
        toast.success(t('members:memberDeletedHistoryKept'));
        setSelectedMember(null);
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || t('members:deleteFailed'));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    } finally {
      setDeleteMemberId(null);
      setDeleteReason("");
      setIsDeleting(false);
    }
  };
  
  const handleToggleStatus = (member, e) => {
    e?.stopPropagation();
    setStatusChangeMember(member);
    setShowStatusModal(true);
  };
  
  const confirmToggleStatus = async () => {
    if (!statusChangeMember) return;
    
    const newStatus = statusChangeMember.status === "inactive" ? "active" : "inactive";
    
    try {
      const response = await fetch(`${API}/members/${statusChangeMember.member_id}/status?status=${newStatus}`, {
        method: "PUT",
        credentials: "include",
      });
      
      if (response.ok) {
        toast.success(newStatus === "active" ? t("admin:memberActivated") : t("admin:memberDeactivated"));
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || t("common:operationFailed"));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    } finally {
      setShowStatusModal(false);
      setStatusChangeMember(null);
    }
  };

  const handleAssignTrainerToPackage = async () => {
    if (!assignTrainerPkg) return;
    setIsAssigningTrainer(true);
    try {
      const trainerId = assignTrainerId === "none" ? null : assignTrainerId;
      const mpId = assignTrainerPkg.member_package_id || assignTrainerPkg.mp_id;
      const res = await fetch(`${API}/member-packages/${mpId}/assign-trainer`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ trainer_id: trainerId }),
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(t('members:trainerAssigned'));
        setMemberPackagesList(prev => prev.map(p => {
          const pId = p.member_package_id || p.mp_id;
          if (pId === mpId) {
            return { ...p, trainer_id: trainerId, trainer_name: data.trainer_name || "" };
          }
          return p;
        }));
        setAssignTrainerPkg(null);
      } else {
        const err = await res.json();
        toast.error(err.detail || t("common:trainerAssignFailed"));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    } finally {
      setIsAssigningTrainer(false);
    }
  };
  
  const openEditDates = (pkg) => {
    const def = packages.find((p) => p.package_id === pkg.package_id);
    const validity_days = pkg.validity_days || def?.validity_days || 30;
    setEditingDatesPkg({
      mp_id: pkg.mp_id || pkg.member_package_id,
      start_date: pkg.start_date || "",
      end_date: pkg.end_date || "",
      validity_days,
    });
  };

  const handleEditStartDate = (newStart) => {
    if (!editingDatesPkg) return;
    let newEnd = editingDatesPkg.end_date;
    if (newStart) {
      try {
        const d = new Date(newStart);
        d.setDate(d.getDate() + (editingDatesPkg.validity_days || 30));
        newEnd = toLocalDateString(d);
      } catch {}
    }
    setEditingDatesPkg({ ...editingDatesPkg, start_date: newStart, end_date: newEnd });
  };

  const saveEditDates = async () => {
    if (!editingDatesPkg) return;
    if (!editingDatesPkg.start_date || !editingDatesPkg.end_date) {
      toast.error(t('common:fieldsRequired', 'Tarihler zorunludur'));
      return;
    }
    if (editingDatesPkg.start_date > editingDatesPkg.end_date) {
      toast.error(t('members:startAfterEnd', 'Başlangıç tarihi bitiş tarihinden sonra olamaz'));
      return;
    }
    setSavingDates(true);
    try {
      const res = await fetch(`${API}/member-packages/${editingDatesPkg.mp_id}/dates`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          start_date: editingDatesPkg.start_date,
          end_date: editingDatesPkg.end_date,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || t('common:operationFailed'));
      }
      toast.success(t('members:datesUpdated', 'Tarihler güncellendi'));
      setEditingDatesPkg(null);
      if (selectedMember) await fetchMemberFullInfo(selectedMember.member_id);
    } catch (e) {
      toast.error(e.message || t('common:errorOccurred'));
    } finally {
      setSavingDates(false);
    }
  };

  const handleSellPackage = async () => {
    if (isSellingPackage) return;
    if (!sellPackageId || !selectedMember) {
      toast.error(t('common:mustSelectPackage'));
      return;
    }
    
    const price = parseInt(sellPrice) || 0;
    const paidAmount = parseInt(sellPaidAmount) || 0;
    
    const isOwner = user?.user_id === businessOwnerId;
    const isManager = currentUserTrainer?.is_manager === true;
    const canDiscount = isOwner || isManager;
    
    const sellPkgTotal = packages.find(p => p.package_id === sellPackageId)?.session_count || 0;
    const transferUsed = parseInt(sellTransferUsed) || 0;
    if (sellIsTransfer && (transferUsed < 0 || transferUsed >= sellPkgTotal)) {
      toast.error(`Daha önce yapılan ders sayısı 0 ile ${Math.max(0, sellPkgTotal - 1)} arasında olmalı`);
      return;
    }
    
    if (!sellIsTransfer && sellMinPrice > 0 && price < sellMinPrice) {
      if (!canDiscount) {
        toast.error(t("common:salePriceMinError", { min: sellMinPrice.toLocaleString() }));
        return;
      }
      if (!sellDiscountReason.trim()) {
        toast.error(t("common:toast_i̇ndirimYaparkenNedenBelirtmelisiniz"));
        return;
      }
    }
    
    if (paidAmount > price) {
      toast.error(t("common:toast_odenenTutarSatisFiyatindanFazla"));
      return;
    }
    
    if (!sellIsTransfer && sellNormalPrice > 0 && price < sellNormalPrice && !sellDiscountReason.trim()) {
      toast.error(t("common:toast_i̇ndirimYaparkenNedenBelirtmelisiniz"));
      return;
    }
    
    try {
      setIsSellingPackage(true);
      const response = await fetch(`${API}/member-packages/sell`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          member_id: selectedMember.member_id,
          package_id: sellPackageId,
          trainer_id: sellTrainer || null,
          price: price,
          paid_amount: paidAmount,
          payment_method: paidAmount > 0 && !sellIsTransfer ? sellPaymentMethod : null,
          business_id: businessId,
          discount_reason: sellDiscountReason.trim() || null,
          sale_date: sellSaleDate || null,
          is_transfer: sellIsTransfer,
          prior_used_sessions: sellIsTransfer ? transferUsed : 0,
          end_date: sellIsTransfer && sellTransferEnd ? sellTransferEnd : null,
        }),
      });
      
      if (response.ok) {
        toast.success(sellIsTransfer ? "Devir paketi kaydedildi" : t("packages:toast_paketSatisiYapildi"), { duration: 2000 });
        setSellIsTransfer(false);
        setSellTransferUsed("0");
        setSellTransferEnd("");
        setShowSellPackageModal(false);
        setSellPackageId("");
        setSellTrainer("");
        setSellPrice("");
        setSellMinPrice(0);
        setSellNormalPrice(0);
        setSellPaidAmount("0");
        setSellPaymentMethod("cash");
        setSellDiscountReason("");
        {
          const dd = new Date();
          setSellSaleDate(`${dd.getFullYear()}-${String(dd.getMonth()+1).padStart(2,'0')}-${String(dd.getDate()).padStart(2,'0')}`);
        }
        setSelectedMember(null);
        fetchData();
        fetchRecentSales();
      } else {
        const error = await response.json();
        toast.error(error.detail || t("common:saleFailed"));
      }
    } catch (error) {
      console.error("Sell package error:", error);
      toast.error(t('common:errorOccurred'));
    } finally {
      setIsSellingPackage(false);
    }
  };

  const validateInvitationCode = async () => {
    if (!invitationCode || invitationCode.length !== 6) {
      toast.error(t("admin:toast_lutfen6HaneliDavetKodunu"));
      return;
    }
    
    setValidatingCode(true);
    try {
      const response = await fetch(`${API}/invitation/validate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ code: invitationCode }),
      });
      
      if (response.ok) {
        const data = await response.json();
        const userData = data.user;
        setValidatedUser(userData);
        toast.success(`${userData.name} ${userData.surname} bulundu!`);
      } else {
        const error = await response.json();
        toast.error(error.detail || t("admin:codeVerifyFailed"));
        setValidatedUser(null);
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
      setValidatedUser(null);
    } finally {
      setValidatingCode(false);
    }
  };

  const handleAddMember = async () => {
    if (isAddingMember) return;
    
    if (addMemberMode === "invitation") {
      if (!validatedUser) {
        toast.error(t("admin:toast_lutfenOnceDavetKodunuDogrulayin"));
        return;
      }
      
      setIsAddingMember(true);

      try {
        const response = await fetch(`${API}/members/add-by-email`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            user_id: validatedUser.user_id,
            phone: validatedUser.phone || null,
            email: validatedUser.email || null,
            business_id: businessId,
            package_id: selectedPackage && selectedPackage !== "none" ? selectedPackage : null,
            trainer_id: selectedTrainerForNew && selectedTrainerForNew !== "none" ? selectedTrainerForNew : null,
            is_candidate: isCandidate && !(selectedPackage && selectedPackage !== "none"),
            sale_price: salePrice ? parseTurkishNumber(salePrice) : null,
            paid_amount: newMemberPaidAmount ? parseTurkishNumber(newMemberPaidAmount) : 0,
            payment_method: parseTurkishNumber(newMemberPaidAmount || 0) > 0 ? newMemberPaymentMethod : null,
            health_notes: healthNotes || null,
            member_card: usedCards.length > 0 ? usedCards[0] : null,
            member_cards: usedCards,
          }),
        });

        if (response.ok) {
          toast.success(t("common:memberAdded"));
          resetAddMemberForm();
          fetchData();
        } else {
          const error = await response.json();
          toast.error(error.detail || t("common:memberAddFailed"));
        }
      } catch (error) {
        toast.error(t('common:errorOccurred'));
      } finally {
        setIsAddingMember(false);
      }
    } else {
      if (!unregisteredMember.name.trim()) {
        toast.error(t("common:memberNameRequired"));
        return;
      }
      if (!unregisteredMember.surname.trim()) {
        toast.error(t("common:memberSurnameRequired"));
        return;
      }
      if (!unregisteredMember.phone.trim()) {
        toast.error(t('common:phoneRequired'));
        return;
      }
      if (!unregisteredMember.gender) {
        toast.error(t("common:toast_cinsiyetSecimiZorunludur"));
        return;
      }
      if (!unregisteredMember.birth_date) {
        toast.error(t('common:birthDateRequired'));
        return;
      }
      
      setIsAddingMember(true);
      
      try {
        const response = await fetch(`${API}/members/add-unregistered`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            business_id: businessId,
            name: unregisteredMember.name.trim(),
            surname: unregisteredMember.surname.trim() || null,
            phone: unregisteredMember.phone.trim() || null,
            email: unregisteredMember.email.trim() || null,
            birth_date: unregisteredMember.birth_date || null,
            gender: unregisteredMember.gender || null,
            package_id: selectedPackage && selectedPackage !== "none" ? selectedPackage : null,
            trainer_id: selectedTrainerForNew && selectedTrainerForNew !== "none" ? selectedTrainerForNew : null,
            is_candidate: isCandidate && !(selectedPackage && selectedPackage !== "none"),
            sale_price: salePrice ? parseTurkishNumber(salePrice) : null,
            paid_amount: newMemberPaidAmount ? parseTurkishNumber(newMemberPaidAmount) : 0,
            payment_method: parseTurkishNumber(newMemberPaidAmount || 0) > 0 ? newMemberPaymentMethod : null,
            health_notes: healthNotes || null,
            member_card: usedCards.length > 0 ? usedCards[0] : null,
            member_cards: usedCards,
          }),
        });
        
        setIsAddingMember(false);
        
        let errorDetail = "";
        try {
          const data = await response.json();
          if (response.ok) {
            toast.success(t("common:toast_kayitOlmayanUyeBasariylaEklendi"));
            resetAddMemberForm();
            fetchData();
            return;
          }
          errorDetail = data?.detail || data?.message || "";
        } catch (e) {
          try {
            errorDetail = await response.clone().text();
          } catch (_) {}
        }
        if (!response.ok) {
          toast.error(errorDetail || `${t("common:memberCouldNotBeAdded")} (HTTP ${response.status})`);
        }
      } catch (error) {
        setIsAddingMember(false);
        toast.error(t("common:toast_baglantiHatasiOlustuLutfenTekrar"));
      }
    }
  };
  
  const resetAddMemberForm = () => {
    setShowAddModal(false);
    setAddMemberMode("invitation");
    setInvitationCode("");
    setValidatedUser(null);
    setUnregisteredMember({
      name: "",
      surname: "",
      phone: "",
      email: "",
      birth_date: "",
      gender: ""
    });
    setSelectedPackage("none");
    setIsCandidate(false);
    setSelectedTrainerForNew("none");
    setSalePrice("");
    setNewMemberPaidAmount("0");
    setNewMemberPaymentMethod("cash");
    setHealthNotes("");
    setUsedCard("none");
    setUsedCards([]);
  };
  
  const handleEditMember = (member, e) => {
    e?.stopPropagation();
    setEditingMember({
      ...member,
      health_notes: member.health_notes || "",
      member_card: member.member_card || "none",
      member_cards: Array.isArray(member.member_cards) ? member.member_cards : (member.member_card ? [member.member_card] : []),
      trainer_id: member.trainer_id || "none",
    });
    setShowEditModal(true);
  };
  
  const handleUpdateMember = async () => {
    if (!editingMember) return;
    if (isSavingEdit) return;
    
    if (editingMember.is_app_registered === false) {
      if (!editingMember.name?.trim()) {
        toast.error(t("common:toast_adAlaniZorunludur"));
        return;
      }
      if (!editingMember.surname?.trim()) {
        toast.error(t("common:toast_soyadAlaniZorunludur"));
        return;
      }
      if (!editingMember.phone?.trim()) {
        toast.error(t("common:toast_telefonAlaniZorunludur"));
        return;
      }
      if (!editingMember.birth_date) {
        toast.error(t('common:birthDateRequired'));
        return;
      }
      if (!editingMember.gender) {
        toast.error(t("common:toast_cinsiyetAlaniZorunludur"));
        return;
      }
    }
    
    setIsSavingEdit(true);
    
    const currentMemberCards = Array.isArray(editingMember.member_cards) 
      ? editingMember.member_cards 
      : (editingMember.member_card && editingMember.member_card !== "none" ? [editingMember.member_card] : []);

    const updatePayload = {
      health_notes: editingMember.health_notes || null,
      member_card: currentMemberCards[0] || null,
      member_cards: currentMemberCards,
      trainer_id: editingMember.trainer_id && editingMember.trainer_id !== "none" ? editingMember.trainer_id : null,
    };
    
    if (editingMember.is_app_registered === false) {
      updatePayload.name = editingMember.name;
      updatePayload.surname = editingMember.surname;
      updatePayload.phone = editingMember.phone;
      updatePayload.email = editingMember.email || null;
      updatePayload.birth_date = editingMember.birth_date || null;
      updatePayload.gender = editingMember.gender || null;
    }
    
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", `${API}/members/${editingMember.member_id}`, true);
    xhr.setRequestHeader("Content-Type", "application/json");
    const authToken = localStorage.getItem("auth_token");
    if (authToken) xhr.setRequestHeader("Authorization", `Bearer ${authToken}`);
    xhr.withCredentials = true;
    
    xhr.onload = async function() {
      setIsSavingEdit(false);
      if (xhr.status === 200) {
        try {
          const updated = JSON.parse(xhr.responseText);
          toast.success(t('members:memberUpdated'));
          setShowEditModal(false);
          setEditingMember(null);
          await fetchData();
          if (selectedMember?.member_id === updated.member_id) {
            setSelectedMember(updated);
          }
        } catch {
          toast.success(t('members:memberUpdated'));
          setShowEditModal(false);
          setEditingMember(null);
          fetchData();
        }
      } else {
        try {
          const errorData = JSON.parse(xhr.responseText);
          toast.error(errorData.detail || t('settings:updateFailed'));
        } catch {
          toast.error(t('settings:updateFailed'));
        }
      }
    };
    
    xhr.onerror = function() {
      setIsSavingEdit(false);
      toast.error(t("common:toast_baglantiHatasiOlustu"));
    };
    
    xhr.send(JSON.stringify(updatePayload));
  };

  const memberIdsWithPackage = useMemo(() => {
    const ids = new Set();
    const memberNos = new Set();
    const names = new Set();

    (allMemberPackages || []).forEach((p) => {
      if (p.status !== "cancelled") {
        if (p.member_id) ids.add(String(p.member_id).trim());
        if (p.client_id) ids.add(String(p.client_id).trim());
        if (p.user_id) ids.add(String(p.user_id).trim());
        if (p.member?.member_id) ids.add(String(p.member.member_id).trim());

        if (p.member_no) memberNos.add(String(p.member_no).trim().toLowerCase());
        if (p.member?.member_no) memberNos.add(String(p.member.member_no).trim().toLowerCase());

        const fullName = `${p.member_name || p.member?.name || ""} ${p.member?.surname || ""}`.trim().toLowerCase();
        if (fullName) names.add(fullName);
      }
    });

    (recentSales?.sales || []).forEach((s) => {
      if (s.status !== "cancelled") {
        if (s.member_id) ids.add(String(s.member_id).trim());
        if (s.client_id) ids.add(String(s.client_id).trim());
        if (s.user_id) ids.add(String(s.user_id).trim());
        if (s.member_no) memberNos.add(String(s.member_no).trim().toLowerCase());
        if (s.member_name) names.add(String(s.member_name).trim().toLowerCase());
      }
    });

    return { ids, memberNos, names };
  }, [allMemberPackages, recentSales]);

  const filteredMembers = members
    .filter((m) => !candidatesOnly || !m.member_no)
    .filter((m) => !restrictedOnly || activeRestriction(m))
    .filter((m) => {
      const cardList = [
        m.member_card,
        m.used_card,
        ...(Array.isArray(m.member_cards) ? m.member_cards : [])
      ].filter(Boolean).filter((c) => c !== "none" && c !== "");
      const hasCard = cardList.length > 0;

      const mNo = String(m.member_no || "").trim().toLowerCase();
      const mFullName = `${m.name || ""} ${m.surname || ""}`.trim().toLowerCase();
      const hasPkg = Boolean(
        memberIdsWithPackage.ids.has(String(m.member_id).trim()) ||
        memberIdsWithPackage.ids.has(String(m.user_id).trim()) ||
        (mNo && memberIdsWithPackage.memberNos.has(mNo)) ||
        (mFullName && memberIdsWithPackage.names.has(mFullName)) ||
        m.is_transfer === true ||
        m.has_transfer_package === true ||
        Number(m.active_packages_count ?? 0) > 0 ||
        Number(m.total_packages ?? 0) > 0 ||
        Number(m.package_count ?? 0) > 0 ||
        Number(m.remaining_sessions ?? 0) > 0 ||
        (Array.isArray(m.packages) && m.packages.length > 0) ||
        (Array.isArray(m.member_packages) && m.member_packages.length > 0) ||
        m.has_active_package === true ||
        m.has_package === true ||
        m.package_id ||
        m.active_package_id ||
        m.current_package_id ||
        m.package_name ||
        m.last_package_name ||
        m.current_package
      );

      const isFromLink = Boolean(
        m.source === "public_booking_link" ||
        m.registration_source === "public_booking_link" ||
        m.registration_type === "public_booking_link" ||
        m.source === "link" ||
        m.registration_source === "link" ||
        m.registered_via_link === true ||
        m.is_link_registered === true ||
        m.link_id ||
        m.invitation_code
      );

      if (memberTypeFilter === "not_registered") {
        const isNotRegistered = m.is_app_registered === false || m.is_unregistered === true || !m.user_id || m.unregistered === true;
        if (!isNotRegistered) return false;

      } else if (memberTypeFilter === "from_link") {
        if (!isFromLink) return false;

      } else if (memberTypeFilter === "with_package") {
        if (!hasPkg) return false;

      } else if (memberTypeFilter === "with_card") {
        if (!hasCard) return false;
      }

      if (cardTypeFilter && cardTypeFilter !== "all") {
        const lowerTarget = cardTypeFilter.toLowerCase().trim();
        const matchesCard = cardList.some((c) => String(c).toLowerCase().trim().includes(lowerTarget));
        if (!matchesCard) return false;
      }

      if (!searchQuery) return true;
      const query = searchQuery.toLowerCase().trim();
      return (
        (m.name || "").toLowerCase().includes(query) ||
        (m.surname || "").toLowerCase().includes(query) ||
        (m.member_no || "").toLowerCase().includes(query) ||
        (m.phone || "").toLowerCase().includes(query) ||
        (m.email || "").toLowerCase().includes(query)
      );
    })
    .sort((a, b) => {
      let comparison = 0;
      switch (sortField) {
        case "name":
          comparison = `${a.name} ${a.surname}`.localeCompare(`${b.name} ${b.surname}`, 'tr');
          break;
        case "member_no":
          comparison = (a.member_no || "").localeCompare(b.member_no || "", 'tr');
          break;
        case "start_date":
          const dateA = a.start_date ? new Date(a.start_date) : new Date(0);
          const dateB = b.start_date ? new Date(b.start_date) : new Date(0);
          comparison = dateA - dateB;
          break;
        case "status":
          comparison = (a.status || "active").localeCompare(b.status || "active");
          break;
        case "trainer":
          const trainerA = trainers.find(t => t.trainer_id === a.trainer_id);
          const trainerB = trainers.find(t => t.trainer_id === b.trainer_id);
          const nameA = trainerA ? `${trainerA.name} ${trainerA.surname}` : "zzz";
          const nameB = trainerB ? `${trainerB.name} ${trainerB.surname}` : "zzz";
          comparison = nameA.localeCompare(nameB, 'tr');
          break;
        default:
          comparison = 0;
      }
      return sortDirection === "asc" ? comparison : -comparison;
    });
  
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };
  
  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ArrowUpDown className="w-4 h-4 ml-1 opacity-50" />;
    return sortDirection === "asc" 
      ? <ArrowUp className="w-4 h-4 ml-1" /> 
      : <ArrowDown className="w-4 h-4 ml-1" />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-navy" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="font-heading font-bold text-xl text-navy">{t('members:title')}</h2>
          <p className="text-grey text-sm">
            {members.length} {statusFilter === "active" ? t('admin:active').toLowerCase() : statusFilter === "inactive" ? t('common:passive').toLowerCase() : ""} {t('common:member').toLowerCase()}
          </p>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[150px] rounded-xl bg-white" data-testid="member-status-filter">
              <SelectValue placeholder={t('common:status')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="active">{t('members:activeMembers')}</SelectItem>
              <SelectItem value="inactive">{t('members:inactiveMembers')}</SelectItem>
              <SelectItem value="all">{t('members:allMembers')}</SelectItem>
            </SelectContent>
          </Select>

          <Select value={memberTypeFilter} onValueChange={setMemberTypeFilter}>
            <SelectTrigger className="w-[165px] rounded-xl bg-white" data-testid="member-type-filter">
              <SelectValue placeholder="Üye Tipi" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tüm Üye Tipleri</SelectItem>
              <SelectItem value="not_registered">Kayıt Olmayan</SelectItem>
              <SelectItem value="from_link">Linkten Gelen</SelectItem>
              <SelectItem value="with_package">Paketli Üyeler</SelectItem>
              <SelectItem value="with_card">Kartlı Üyeler</SelectItem>
            </SelectContent>
          </Select>

          <Select value={cardTypeFilter} onValueChange={setCardTypeFilter}>
            <SelectTrigger className={`w-[155px] rounded-xl ${cardTypeFilter !== 'all' ? 'bg-amber-50 border-amber-300 text-amber-900 font-semibold' : 'bg-white'}`} data-testid="member-card-filter">
              <SelectValue placeholder="Kartlar" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tüm Kartlar</SelectItem>
              {(() => {
                const availableCards = (memberCards && memberCards.length > 0)
                  ? memberCards.map(c => typeof c === 'string' ? c : c.name)
                  : ["MultiSport", "Wellclub", "Fitpass", "Sporcard"];
                
                return Array.from(new Set(availableCards)).map((cName) => (
                  <SelectItem key={cName} value={cName}>{cName}</SelectItem>
                ));
              })()}
            </SelectContent>
          </Select>
          
          <Button variant={candidatesOnly ? "default" : "outline"} className={`rounded-xl ${candidatesOnly ? "bg-amber-500 hover:bg-amber-600 text-white" : "text-amber-700 border-amber-200"}`}
            onClick={() => setCandidatesOnly((v) => !v)} data-testid="member-candidates-filter-btn">
            Adaylar
          </Button>
          <Button variant={restrictedOnly ? "default" : "outline"} className={`rounded-xl ${restrictedOnly ? "bg-red-600 hover:bg-red-700 text-white" : "text-red-700 border-red-200"}`}
            onClick={() => setRestrictedOnly((v) => !v)} data-testid="member-restricted-filter-btn">
            Kısıtlılar
          </Button>
          
          <Button
            variant="outline"
            className="rounded-xl border-emerald-200 text-emerald-700 hover:bg-emerald-50"
            onClick={() => setShowRecentSalesModal(true)}
            data-testid="recent-sales-btn"
          >
            <TrendingUp className="w-4 h-4 mr-2" />
            {t('members:recentSales')}
          </Button>

          <div className="flex border rounded-xl overflow-hidden">
            <Button 
              variant={viewMode === "list" ? "default" : "ghost"} 
              size="sm" 
              onClick={() => setViewMode("list")} 
              className={`rounded-none ${viewMode === "list" ? "bg-navy text-white" : ""}`}
            >
              <List className="w-4 h-4" />
            </Button>
            <Button 
              variant={viewMode === "grid" ? "default" : "ghost"} 
              size="sm" 
              onClick={() => setViewMode("grid")} 
              className={`rounded-none ${viewMode === "grid" ? "bg-navy text-white" : ""}`}
            >
              <LayoutGrid className="w-4 h-4" />
            </Button>
          </div>

          <div className="flex bg-slate-200/80 p-1 rounded-2xl w-fit">
            <button
              type="button"
              onClick={() => setActiveTab("members")}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all ${
                activeTab === "members"
                  ? "bg-white text-navy shadow-sm"
                  : "text-stone-600 hover:text-navy"
              }`}
            >
              <Users className="w-4 h-4" />
              Üyeler
              <span className="text-xs font-normal text-stone-400">({members.length})</span>
            </button>

            <button
              type="button"
              onClick={() => setActiveTab("demo_requests")}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all ${
                activeTab === "demo_requests"
                  ? "bg-white text-navy shadow-sm"
                  : "text-stone-600 hover:text-navy"
              }`}
            >
              <Sparkles className="w-4 h-4 text-amber-500" />
              Demo Talepleri
              {demoRequests.filter((r) => r.status === "pending" || r.status === "in_progress").length > 0 && (
                <span className="bg-amber-500 text-white text-[11px] font-bold px-2 py-0.5 rounded-full animate-pulse">
                  {demoRequests.filter((r) => r.status === "pending" || r.status === "in_progress").length}
                </span>
              )}
            </button>
          </div>

          {canEdit && (
            <Dialog open={showAddModal} onOpenChange={(open) => {
              setShowAddModal(open);
              if (!open) {
                resetAddMemberForm();
              }
            }}>
              <DialogTrigger asChild>
                <Button
                  className="bg-navy hover:bg-navy/90 rounded-xl"
                  data-testid="add-member-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  {t('members:newMember')}
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg w-[95vw] sm:w-full max-h-[90vh] overflow-y-auto [&>button:last-child]:hidden pb-6">
                <DialogHeader>
                  <DialogTitle>{t('members:addNewMember')}</DialogTitle>
                  <DialogDescription>
                    {addMemberMode === "invitation" 
                      ? t('admin:enterInvitationCode')
                      : t('members:addUnregisteredMember')
                    }
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div className="flex border rounded-xl overflow-hidden">
                    <button
                      onClick={() => {
                        setAddMemberMode("invitation");
                        setValidatedUser(null);
                        setInvitationCode("");
                      }}
                      className={`flex-1 py-2.5 px-4 text-sm font-medium transition-colors ${
                        addMemberMode === "invitation" 
                          ? "bg-navy text-white" 
                          : "bg-slate-50 text-slate-600 hover:bg-slate-100"
                      }`}
                    >
                      {t("settings:withInvitationCode")}
                    </button>
                    <button
                      onClick={() => {
                        setAddMemberMode("unregistered");
                        setValidatedUser(null);
                        setInvitationCode("");
                      }}
                      className={`flex-1 py-2.5 px-4 text-sm font-medium transition-colors ${
                        addMemberMode === "unregistered" 
                          ? "bg-navy text-white" 
                          : "bg-slate-50 text-slate-600 hover:bg-slate-100"
                      }`}
                    >
                      {t("admin:unregisteredMember")}
                    </button>
                  </div>

                  {addMemberMode === "invitation" ? (
                    <>
                      <div className="space-y-2">
                        <Label>{t("members:invitationCode")}</Label>
                        <div className="flex gap-2">
                          <Input
                            placeholder={t("admin:sixDigitCode")}
                            value={invitationCode}
                            onChange={(e) => {
                              const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                              setInvitationCode(value);
                              if (value.length < 6) {
                                setValidatedUser(null);
                              }
                            }}
                            className="text-center text-xl font-mono tracking-[0.3em] rounded-xl"
                            maxLength={6}
                            disabled={validatedUser !== null}
                            data-testid="invitation-code-input"
                          />
                          {!validatedUser ? (
                            <Button
                              onClick={validateInvitationCode}
                              disabled={invitationCode.length !== 6 || validatingCode}
                              className="bg-navy hover:bg-navy/90 rounded-xl px-6"
                              data-testid="validate-code-btn"
                            >
                              {validatingCode ? "..." : t('members:verify')}
                            </Button>
                          ) : (
                            <Button
                              variant="outline"
                              onClick={() => {
                                setValidatedUser(null);
                                setInvitationCode("");
                              }}
                              className="rounded-xl px-4"
                            >
                              {t("common:change")}
                            </Button>
                          )}
                        </div>
                        <p className="text-xs text-gray-500">
                          {t("admin:memberCanGetCodeNote")}
                        </p>
                      </div>
                  
                      {validatedUser && (
                        <div className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-xl">
                          <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center text-white font-bold">
                            {validatedUser.name?.[0]}{validatedUser.surname?.[0]}
                          </div>
                          <div className="flex-1">
                            <p className="font-medium text-green-800">
                              {validatedUser.name} {validatedUser.surname}
                            </p>
                            <p className="text-sm text-green-600">{validatedUser.email}</p>
                            {validatedUser.phone && (
                              <p className="text-xs text-green-600">{validatedUser.phone}</p>
                            )}
                          </div>
                          <CheckCircle2 className="w-6 h-6 text-green-500" />
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="space-y-4">
                      <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-800">
                        {t("admin:unregisteredMemberNote2")}
                      </div>
                      
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label>{t("common:firstName")} <span className="text-red-500">*</span></Label>
                          <Input
                            placeholder={t("common:memberName")}
                            value={unregisteredMember.name}
                            onChange={(e) => setUnregisteredMember({...unregisteredMember, name: e.target.value})}
                            className="rounded-xl"
                            data-testid="unregistered-name"
                            required
                          />
                        </div>
                        <div className="space-y-1">
                          <Label>{t("common:surname")} <span className="text-red-500">*</span></Label>
                          <Input
                            placeholder={t("common:memberSurname")}
                            value={unregisteredMember.surname}
                            onChange={(e) => setUnregisteredMember({...unregisteredMember, surname: e.target.value})}
                            className="rounded-xl"
                            data-testid="unregistered-surname"
                            required
                          />
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label>{t("common:phone")} <span className="text-red-500">*</span></Label>
                          <Input
                            placeholder="05XX XXX XX XX"
                            value={unregisteredMember.phone}
                            onChange={(e) => setUnregisteredMember({...unregisteredMember, phone: e.target.value})}
                            className="rounded-xl"
                            data-testid="unregistered-phone"
                            required
                          />
                        </div>
                        <div className="space-y-1">
                          <Label>{t('common:email')}</Label>
                          <Input
                            type="email"
                            placeholder="ornek@email.com"
                            value={unregisteredMember.email}
                            onChange={(e) => setUnregisteredMember({...unregisteredMember, email: e.target.value})}
                            className="rounded-xl"
                            data-testid="unregistered-email"
                          />
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label>{t("common:birthDate")} <span className="text-red-500">*</span></Label>
                          <Popover open={unregisteredBirthDateOpen} onOpenChange={setUnregisteredBirthDateOpen}>
                            <PopoverTrigger asChild>
                              <Button
                                variant="outline"
                                className={cn(
                                  "w-full justify-start text-left font-normal rounded-xl",
                                  !unregisteredMember.birth_date && "text-muted-foreground"
                                )}
                                data-testid="unregistered-birth-date-trigger"
                              >
                                <Calendar className="mr-2 h-4 w-4" />
                                {unregisteredMember.birth_date ? (
                                  format(new Date(unregisteredMember.birth_date), "dd.MM.yyyy", { locale: getDateLocale() })
                                ) : (
                                  <span>{t('common:selectDate')}</span>
                                )}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0" align="start">
                              <CalendarComponent
                                mode="single"
                                selected={unregisteredMember.birth_date ? new Date(unregisteredMember.birth_date) : undefined}
                                onSelect={(date) => {
                                  if (date) {
                                    setUnregisteredMember({
                                      ...unregisteredMember,
                                      birth_date: format(date, "yyyy-MM-dd")
                                    });
                                    setUnregisteredBirthDateOpen(false);
                                  }
                                }}
                                defaultMonth={unregisteredMember.birth_date ? new Date(unregisteredMember.birth_date) : new Date()}
                                fromYear={1940}
                                toYear={new Date().getFullYear()}
                                initialFocus={false}
                                locale={tr}
                              />
                            </PopoverContent>
                          </Popover>
                        </div>
                        <div className="space-y-1">
                          <Label>{t("common:gender")} <span className="text-red-500">*</span></Label>
                          <Select 
                            value={unregisteredMember.gender} 
                            onValueChange={(val) => setUnregisteredMember({...unregisteredMember, gender: val})}
                            required
                          >
                            <SelectTrigger className="rounded-xl">
                              <SelectValue placeholder={t("common:ph_secin")} />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="male">{t('common:male')}</SelectItem>
                              <SelectItem value="female">{t('common:female')}</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {(validatedUser || addMemberMode === "unregistered") && (
                    <>
                      {!(selectedPackage && selectedPackage !== "none") && (
                        <label className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-xl cursor-pointer" data-testid="member-candidate-option">
                          <input type="checkbox" className="mt-1 w-4 h-4 accent-amber-600" checked={isCandidate} onChange={(e) => setIsCandidate(e.target.checked)} data-testid="member-candidate-checkbox" />
                          <span className="text-sm text-amber-900"><b>Demo için aday üye</b> — üye numarası verilmez. İlk paket satışında ya da ilk kartlı (ücretli) derste numara otomatik verilir.</span>
                        </label>
                      )}
                      <div>
                        <Label>Paket (Opsiyonel)</Label>
                        {packagesWithPrice.length > 0 ? (
                          <Select value={selectedPackage} onValueChange={setSelectedPackage}>
                            <SelectTrigger className="rounded-xl">
                              <SelectValue placeholder={t("packages:ph_paketSecin")} />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">Paket Yok</SelectItem>
                              {packagesWithPrice.map((pkg) => {
                                const priceInfo = activePriceList?.prices?.find(p => p.package_id === pkg.package_id);
                                return (
                                  <SelectItem key={pkg.package_id} value={pkg.package_id}>
                                    {pkg.name} - {currencySymbol}{priceInfo?.price || pkg.price || 0}
                                  </SelectItem>
                                );
                              })}
                            </SelectContent>
                          </Select>
                        ) : (
                          <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-800">
                            {t("packages:priceListRequired")}
                          </div>
                        )}
                      </div>

                      {selectedPackage && selectedPackage !== "none" && (
                        <>
                          {(() => {
                            const selectedPkg = packagesWithPrice.find(p => p.package_id === selectedPackage);
                            const priceInfo = activePriceList?.prices?.find(p => p.package_id === selectedPackage);
                            const originalPrice = priceInfo?.price || priceInfo?.normal_price || selectedPkg?.price || 0;
                            const minPrice = priceInfo?.min_price || priceInfo?.discounted_price || 0;
                            const isManager = currentUserTrainer?.is_manager === true;
                            const isBusinessOwner = user?.role === "business" || user?.user_id === businessOwnerId;
                            const canFullDiscount = isManager || isBusinessOwner;
                            
                            const allowedMinPrice = canFullDiscount ? 0 : (minPrice > 0 ? minPrice : originalPrice);
                            const allowedMaxPrice = canFullDiscount ? Infinity : originalPrice;
                            
                            const currentSalePrice = parseTurkishNumber(salePrice) || 0;
                            const isDiscounted = currentSalePrice > 0 && currentSalePrice < originalPrice;
                            
                            return (
                              <>
                                <div>
                                  <Label>{t('payments:salePrice')}</Label>
                                  <Input
                                    type="number"
                                    placeholder={currencySymbol}
                                    value={salePrice}
                                    onChange={(e) => {
                                      const inputValue = e.target.value;
                                      if (inputValue === "" || inputValue === null) {
                                        setSalePrice("");
                                        return;
                                      }
                                      setSalePrice(inputValue);
                                    }}
                                    onBlur={(e) => {
                                      const finalPrice = parseTurkishNumber(e.target.value) || 0;
                                      if (finalPrice > 0) {
                                        if (finalPrice < allowedMinPrice) {
                                          toast.error(`${t("settings:priceLabel")} min: ${currencySymbol}${allowedMinPrice.toLocaleString()}`);
                                          setSalePrice(String(allowedMinPrice));
                                        } else if (!canFullDiscount && finalPrice > allowedMaxPrice) {
                                          toast.error(`${t("settings:priceLabel")} max: ${currencySymbol}${allowedMaxPrice.toLocaleString()}`);
                                          setSalePrice(String(originalPrice));
                                        }
                                      }
                                    }}
                                    className="rounded-xl"
                                  />
                                  {!canFullDiscount && minPrice > 0 && (
                                    <p className="text-xs text-blue-600 mt-1">
                                      💡 Min: {currencySymbol}{minPrice.toLocaleString('tr-TR')} - Max: {currencySymbol}{originalPrice.toLocaleString('tr-TR')}
                                    </p>
                                  )}
                                  {!canFullDiscount && minPrice === 0 && (
                                    <p className="text-xs text-amber-600 mt-1">
                                      ⚠️ İndirim yetkiniz yok. Fiyat: {currencySymbol}{originalPrice.toLocaleString('tr-TR')}
                                    </p>
                                  )}
                                  {canFullDiscount && minPrice > 0 && (
                                    <p className="text-xs text-green-600 mt-1">
                                      ✓ Min fiyat: {currencySymbol}{minPrice.toLocaleString('tr-TR')} (Yönetici olarak altında satabilirsiniz)
                                    </p>
                                  )}
                                  {isDiscounted && canFullDiscount && minPrice === 0 && (
                                    <p className="text-xs text-green-600 mt-1">
                                      ✓ İndirimli: {currencySymbol}{(originalPrice - currentSalePrice).toLocaleString('tr-TR')} indirim
                                    </p>
                                  )}
                                </div>
                              </>
                            );
                          })()}
                          
                          <div>
                            <Label>{t("common:paidAmountLabel")} ({currencySymbol})</Label>
                            <Input
                              type="number"
                              min="0"
                              placeholder="0"
                              value={newMemberPaidAmount}
                              onChange={(e) => setNewMemberPaidAmount(e.target.value)}
                              className="rounded-xl"
                            />
                            {salePrice && parseTurkishNumber(newMemberPaidAmount || 0) < parseTurkishNumber(salePrice) && (
                              <p className="text-xs text-amber-600 mt-1">
                                {t('packages:remainingAmount2')}: {currencySymbol}{(parseTurkishNumber(salePrice) - parseTurkishNumber(newMemberPaidAmount || 0)).toLocaleString('tr-TR')}
                              </p>
                            )}
                          </div>
                          
                          {parseTurkishNumber(newMemberPaidAmount || 0) > 0 && (
                            <div>
                              <Label>{t('payments:paymentMethod')}</Label>
                              <Select value={newMemberPaymentMethod} onValueChange={setNewMemberPaymentMethod}>
                                <SelectTrigger className="rounded-xl">
                                  <SelectValue placeholder={t("common:selectPaymentMethod")} />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="cash">{t('payments:cash')}</SelectItem>
                                  <SelectItem value="credit_card">{t('payments:creditCard')}</SelectItem>
                                  <SelectItem value="bank_transfer">{t('payments:bankTransfer')}</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          )}
                          
                          <div>
                            <Label>{t("members:trainerOptional")}</Label>
                            <Select value={selectedTrainerForNew} onValueChange={setSelectedTrainerForNew}>
                              <SelectTrigger className="rounded-xl">
                                <SelectValue placeholder={t("common:selectTrainer")} />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">{t('trainers:noTrainer')}</SelectItem>
                                {trainers.filter(t => t.is_active !== false).map((trainer) => (
                                  <SelectItem key={trainer.trainer_id} value={trainer.trainer_id}>
                                    {trainer.name} {trainer.surname}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </>
                      )}

                      <div>
                        <Label className="block mb-1.5">{t('members:usedCard')} (Çoklu Seçilebilir)</Label>
                        {memberCards.length > 0 ? (
                          <div className="flex flex-wrap gap-2 p-2 bg-slate-50 border rounded-xl">
                            {memberCards.map((card) => {
                              const isSelected = usedCards.includes(card);
                              return (
                                <button
                                  key={card}
                                  type="button"
                                  onClick={() => {
                                    setUsedCards(prev => 
                                      isSelected ? prev.filter(c => c !== card) : [...prev, card]
                                    );
                                  }}
                                  className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                                    isSelected 
                                      ? "bg-navy text-white border-navy" 
                                      : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"
                                  }`}
                                >
                                  {card} {isSelected && "✓"}
                                </button>
                              );
                            })}
                          </div>
                        ) : (
                          <p className="text-xs text-grey italic">Tanımlı kart bulunamadı.</p>
                        )}
                      </div>

                      <div>
                        <Label>{t('members:healthNotes')}</Label>
                        <Textarea
                          placeholder={t("common:ph_hastalikSakatlikVbNotlar")}
                          value={healthNotes}
                          onChange={(e) => setHealthNotes(e.target.value)}
                          className="rounded-xl"
                        />
                      </div>

                      <Button
                        className="w-full bg-navy hover:bg-navy/90 rounded-xl h-11 text-sm font-semibold"
                        onClick={handleAddMember}
                        disabled={isAddingMember}
                      >
                        {isAddingMember ? t("common:adding") : t("common:addMember")}
                      </Button>
                    </>
                  )}

                  <div className="pt-2 border-t">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => resetAddMemberForm()}
                      className="w-full rounded-xl h-11 border-stone-300 text-stone-700 hover:bg-slate-100 font-semibold text-sm"
                    >
                      Vazgeç / Kapat
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </div>
        
        {!canEdit && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-800">
            {t("surveys:subscriptionRequiredForMember")}
          </div>
        )}
      </div>

      {activeTab === "members" && (
        <>
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-grey" />
                <Input
                  placeholder={t("common:searchMember")}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 rounded-xl"
                  data-testid="search-members"
                />
              </div>
            </CardContent>
          </Card>

          <LinkPendingPanel businessId={businessId} onChanged={fetchData} refreshKey={members.length} />
          <MemberAlerts businessId={businessId} refreshKey={members.length} onChanged={fetchData} onSell={openSellPackageModal} sellSignal={showSellPackageModal} onBadges={(d) => setPkgReqIds(d?.package_request_member_ids || [])} />

          {filteredMembers.length > 0 ? (
            viewMode === 'list' ? (
              <>
                <Card className="rounded-2xl hidden md:block overflow-hidden border-0 shadow-sm">
                  <div className="px-4 py-3 border-b bg-slate-50/80 flex items-center justify-between">
                    <p className="text-sm font-semibold text-navy">
                      Listelenen Üyeler: <span className="font-bold text-navy">{filteredMembers.length}</span>
                      {filteredMembers.length !== members.length && (
                        <span className="text-xs text-grey font-normal ml-1">
                          (toplam {members.length} üye içerisinden filtrelendi)
                        </span>
                      )}
                    </p>
                  </div>

                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead 
                          className="cursor-pointer hover:bg-slate-100"
                          onClick={() => handleSort("name")}
                        >
                          <span className="flex items-center">
                            {t('common:member')} <SortIcon field="name" />
                          </span>
                        </TableHead>
                        <TableHead 
                          className="cursor-pointer hover:bg-slate-100"
                          onClick={() => handleSort("member_no")}
                        >
                          <span className="flex items-center">
                            {t("members:memberNo")} <SortIcon field="member_no" />
                          </span>
                        </TableHead>
                        <TableHead 
                          className="cursor-pointer hover:bg-slate-100"
                          onClick={() => handleSort("trainer")}
                        >
                          <span className="flex items-center">
                            {t("common:trainer")} <SortIcon field="trainer" />
                          </span>
                        </TableHead>
                        <TableHead 
                          className="cursor-pointer hover:bg-slate-100"
                          onClick={() => handleSort("start_date")}
                        >
                          <span className="flex items-center">
                            {t("members:registrationDate")} <SortIcon field="start_date" />
                          </span>
                        </TableHead>
                        <TableHead>{t('business:rating')}</TableHead>
                        <TableHead 
                          className="cursor-pointer hover:bg-slate-100"
                          onClick={() => handleSort("status")}
                        >
                          <span className="flex items-center">
                            {t("members:statusCol")} <SortIcon field="status" />
                          </span>
                        </TableHead>
                        <TableHead className="text-right">{t('common:actions')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredMembers.map((member) => (
                        <TableRow
                          key={member.member_id}
                          className="cursor-pointer hover:bg-slate-50"
                          onClick={() => handleMemberClick(member)}
                          data-testid={`member-row-${member.member_id}`}
                        >
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <Avatar className="w-8 h-8">
                                <AvatarFallback className="bg-navy/10 text-navy text-xs font-bold">
                                  {member.name?.charAt(0)}
                                </AvatarFallback>
                              </Avatar>
                              <div>
                                <div className="flex items-center gap-2">
                                  <p className="font-medium text-navy">{member.name} {member.surname}</p>
                                  {member.is_app_registered === false && (
                                    <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-orange-600 border-orange-300 bg-orange-50">{t("members:notRegistered")}
                                    </Badge>
                                  )}
                                  <MemberFlags member={member} pkgReqIds={pkgReqIds} />
                                </div>
                                <div className="text-xs text-grey flex flex-wrap items-center gap-x-1.5 mt-0.5">
                                  {member.phone && <span>{member.phone}</span>}
                                  {member.phone && member.email && <span>·</span>}
                                  {member.email && <span>{member.email}</span>}
                                </div>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            {member.member_no ? (<code className="text-sm bg-slate-100 px-2 py-1 rounded">
                              {member.member_no}
                            </code>) : <span className="text-xs font-semibold bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded" data-testid="member-candidate-badge">Aday</span>}
                          </TableCell>
                          <TableCell>
                            {member.trainer_id ? (
                              <Badge variant="secondary" className="bg-purple-100 text-purple-700">
                                {(() => {
                                  const trainer = trainers.find(t => t.trainer_id === member.trainer_id);
                                  return trainer ? `${trainer.name} ${trainer.surname}` : "-";
                                })()}
                              </Badge>
                            ) : (
                              <span className="text-grey">-</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <span className="text-sm">{member.start_date ? format(new Date(member.start_date), "dd.MM.yyyy", { locale: getDateLocale() }) : "-"}</span>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                              <span>{member.rating > 0 ? member.rating.toFixed(1) : "5.0"}</span>
                              <span className="text-grey text-xs">({member.rating_count || 0})</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge
                              variant={member.status === "inactive" ? "secondary" : "default"}
                              className={member.status === "inactive" ? "bg-slate-100 text-slate-500" : "bg-green-100 text-green-700"}
                            >
                              {member.status === "inactive" ? t('common:passive') : t('admin:active')}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                            <div className="flex items-center justify-end gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openSellPackageModal(member);
                                }}
                                className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                                title={t('payments:sellPackage')}
                                data-testid={`sell-package-${member.member_id}`}
                              >
                                <Package className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => handleEditMember(member, e)}
                                className="text-grey hover:text-navy"
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              {canDeactivateMembers && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => handleToggleStatus(member, e)}
                                className={member.status === "inactive" 
                                  ? "text-green-500 hover:text-green-700 hover:bg-green-50" 
                                  : "text-amber-500 hover:text-amber-700 hover:bg-amber-50"}
                                title={member.status === "inactive" ? t("admin:makeActive") : t("common:makePassive2")}
                                data-testid={`toggle-status-${member.member_id}`}
                              >
                                {member.status === "inactive" ? (
                                  <CheckCircle2 className="w-4 h-4" />
                                ) : (
                                  <AlertCircle className="w-4 h-4" />
                                )}
                              </Button>
                              )}
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => handleDeleteMember(member.member_id, e)}
                                className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                data-testid={`delete-member-${member.member_id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                              <ChevronRight className="w-4 h-4 text-grey" />
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Card>

                <div className="md:hidden space-y-3">
                  {filteredMembers.map((member) => (
                    <Card
                      key={member.member_id}
                      className="border-0 shadow-sm cursor-pointer active:bg-slate-50"
                      onClick={() => handleMemberClick(member)}
                      data-testid={`member-mobile-${member.member_id}`}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                          <Avatar className="h-10 w-10 flex-shrink-0">
                            <AvatarFallback className="bg-navy text-white text-sm">
                              {member.name?.charAt(0)}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-navy text-sm">
                              {member.name} {member.surname}
                            </p>
                            <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                              {member.member_no ? (<code className="text-xs bg-slate-100 px-1 py-0.5 rounded">
                                {member.member_no}
                              </code>) : <span className="text-xs font-semibold bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded" data-testid="member-candidate-badge">Aday</span>}
                              {member.is_app_registered === false && (
                                <Badge variant="outline" className="text-[9px] px-1 py-0 text-orange-600 border-orange-300 bg-orange-50">{t("members:notRegistered")}
                                </Badge>
                              )}
                              <MemberFlags member={member} pkgReqIds={pkgReqIds} />
                              <Badge
                                variant={member.status === "inactive" ? "secondary" : "default"}
                                className={`text-[9px] px-1 py-0 ${member.status === "inactive" ? "bg-slate-100 text-slate-500" : "bg-green-100 text-green-700"}`}
                              >
                                {member.status === "inactive" ? t('common:passive') : t('admin:active')}
                              </Badge>
                            </div>
                          </div>
                          <div className="flex items-center gap-0.5 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0"
                              onClick={(e) => {
                                e.stopPropagation();
                                openSellPackageModal(member);
                              }}
                              title={t('payments:sellPackage')}
                              data-testid={`sell-package-mobile-${member.member_id}`}
                            >
                              <Package className="w-4 h-4 text-emerald-600" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0"
                              onClick={(e) => handleEditMember(member, e)}
                              title={t('common:edit')}
                            >
                              <Edit className="w-4 h-4 text-grey" />
                            </Button>
                            <ChevronRight className="w-4 h-4 text-grey ml-1" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredMembers.map((member) => (
                  <Card
                    key={member.member_id}
                    className="border-0 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                    onClick={() => handleSelectMember(member)}
                    data-testid={`member-card-${member.member_id}`}
                  >
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <Avatar className="h-12 w-12">
                            <AvatarFallback className="bg-navy text-white">
                              {member.name?.charAt(0)}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="font-heading font-bold text-lg text-navy">
                                {member.name} {member.surname}
                              </h3>
                              {member.is_app_registered === false && (
                                <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-orange-600 border-orange-300 bg-orange-50">{t("members:notRegistered")}
                                </Badge>
                              )}
                              <MemberFlags member={member} pkgReqIds={pkgReqIds} />
                            </div>
                            <div className="flex items-center gap-2 mt-1">
                              {member.member_no ? (<code className="text-xs bg-slate-100 px-1.5 py-0.5 rounded">
                                {member.member_no}
                              </code>) : <span className="text-xs font-semibold bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded" data-testid="member-candidate-badge">Aday</span>}
                            </div>
                            
                            <div className="mt-2 text-xs text-grey space-y-0.5">
                              {member.phone && <p className="flex items-center gap-1"><Phone className="w-3 h-3 text-grey/70" /> {member.phone}</p>}
                              {member.email && <p className="flex items-center gap-1 truncate max-w-[200px]"><Mail className="w-3 h-3 text-grey/70" /> {member.email}</p>}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-8 w-8"
                            onClick={(e) => {
                              e.stopPropagation();
                              openSellPackageModal(member);
                            }}
                            title={t('payments:sellPackage')}
                            data-testid={`sell-package-grid-${member.member_id}`}
                          >
                            <Package className="w-4 h-4 text-emerald-600" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-8 w-8"
                            onClick={(e) => handleEditMember(member, e)}
                          >
                            <Edit className="w-4 h-4 text-grey" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-8 w-8"
                            onClick={(e) => handleDeleteMember(member.member_id, e)}
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      </div>

                      {member.trainer_id && (
                        <div className="mb-3">
                          <p className="text-xs text-grey mb-1.5">{t('common:trainer')}</p>
                          <Badge variant="secondary" className="bg-purple-100 text-purple-700">
                            {(() => {
                              const trainer = trainers.find(t => t.trainer_id === member.trainer_id);
                              return trainer ? `${trainer.name} ${trainer.surname}` : "-";
                            })()}
                          </Badge>
                        </div>
                      )}

                      {(member.member_card || member.used_card) && (
                        <div className="mb-3">
                          <p className="text-xs text-grey mb-1.5">{t('members:membershipCard')}</p>
                          <div className="flex items-center gap-2">
                            <CreditCard className="w-4 h-4 text-grey" />
                            <Badge variant="secondary">{member.member_card || member.used_card}</Badge>
                          </div>
                        </div>
                      )}

                      <div className="flex items-center justify-between pt-2 border-t">
                        <span className="text-xs text-grey">
                          {member.start_date ? format(new Date(member.start_date), "dd.MM.yyyy", { locale: getDateLocale() }) : "-"}
                        </span>
                        <Badge
                          variant={member.status === "inactive" ? "secondary" : "default"}
                          className={member.status === "inactive" ? "bg-slate-100 text-slate-500" : "bg-green-100 text-green-700"}
                        >
                          {member.status === "inactive" ? t('common:passive') : t('admin:active')}
                        </Badge>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )
          ) : (
            <Card className="border-0 shadow-sm">
              <CardContent className="p-12 text-center">
                <Users className="w-12 h-12 text-grey mx-auto mb-3" />
                <p className="text-grey">
                  {restrictedOnly ? "Kısıtlı üye bulunmuyor" : candidatesOnly ? "Aday üye bulunmuyor" : searchQuery ? t("common:memberNotFound") : t("common:noMembersYet")}
                </p>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {activeTab === "demo_requests" && (
        <div className="space-y-4">
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4 space-y-3">
              <div className="relative w-full">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-grey" />
                <Input
                  placeholder="İsim veya telefon numarası ile ara..."
                  value={demoSearchQuery}
                  onChange={(e) => setDemoSearchQuery(e.target.value)}
                  className="pl-10 rounded-xl h-11 w-full"
                />
              </div>

              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-2 flex-wrap">
                  <Select value={demoStatusFilter} onValueChange={setDemoStatusFilter}>
                    <SelectTrigger className="w-[190px] rounded-xl h-10">
                      <SelectValue placeholder="Tüm Durumlar" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tüm Durumlar</SelectItem>
                      <SelectItem value="pending">🟡 Yeni Bekleyen</SelectItem>
                      <SelectItem value="in_progress">🔵 Aranıyor (1-2 Arama)</SelectItem>
                      <SelectItem value="called">🟢 Görüşüldü</SelectItem>
                      <SelectItem value="unreachable">🔴 Ulaşılamadı (3/3)</SelectItem>
                      <SelectItem value="demo_scheduled">📅 Demo Planlandı</SelectItem>
                      <SelectItem value="converted">🎉 Satış Yapıldı</SelectItem>
                      <SelectItem value="cancelled">⚪ İptal</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <Button
                  variant="outline"
                  onClick={() => setDemoSortAsc(!demoSortAsc)}
                  className="rounded-xl h-10 text-xs font-medium"
                >
                  <ArrowUpDown className="w-4 h-4 mr-1.5" />
                  {demoSortAsc ? "Tarihe Göre: Eskiden Yeniye" : "Tarihe Göre: Yeniden Eskiye"}
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-2xl border-0 shadow-sm overflow-hidden hidden md:block">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>Aday Bilgisi</TableHead>
                  <TableHead>İletişim</TableHead>
                  <TableHead>Talep Tarihi</TableHead>
                  <TableHead className="text-center">Arama Hakkı</TableHead>
                  <TableHead>Durum</TableHead>
                  <TableHead>Son Not</TableHead>
                  <TableHead className="text-right">Aksiyon</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
              {demoRequests
                .filter((req) => {
                  if (demoStatusFilter !== "all" && req.status !== demoStatusFilter) return false;
                  if (!demoSearchQuery) return true;
                  const q = demoSearchQuery.toLowerCase();
                  return (
                    req.name?.toLowerCase().includes(q) ||
                    req.surname?.toLowerCase().includes(q) ||
                    req.phone?.includes(q)
                  );
                })
                .sort((a, b) => {
                  const dateA = new Date(a.created_at || 0);
                  const dateB = new Date(b.created_at || 0);
                  return demoSortAsc ? dateA - dateB : dateB - dateA;
                })
                .map((req) => {
                  const callCount = req.call_count || (req.call_logs ? req.call_logs.length : 0);
                  return (
                    <TableRow
                      key={req.demo_id}
                      className="cursor-pointer hover:bg-slate-50 transition-colors"
                      onClick={() => {
                        setSelectedDemoLead(req);
                        setCallLogForm({ result: "spoke", note: "" });
                        setOutcomeForm({ type: "callback", note: "", scheduled_date: "" });
                      }}
                    >
                      <TableCell>
                        <div>
                          <p className="font-bold text-navy text-sm">{req.name} {req.surname}</p>
                          {req.branch && (
                            <Badge variant="outline" className="text-[10px] mt-0.5 bg-emerald-50 text-emerald-800 border-emerald-200">
                              {req.branch}
                            </Badge>
                          )}
                        </div>
                      </TableCell>

                      <TableCell>
                        <div className="flex items-center gap-2">
                          <a
                            href={getWhatsAppLink(req.phone)}
                            target="_blank"
                            rel="noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-emerald-50 text-emerald-700 hover:bg-emerald-100 text-xs font-semibold transition-colors"
                          >
                            <MessageCircle className="w-3.5 h-3.5 text-emerald-600" />
                            WhatsApp Aç
                          </a>
                          <span className="text-xs text-stone-600 font-medium tracking-wide">
                            {req.phone}
                          </span>
                        </div>
                      </TableCell>

                      <TableCell className="text-xs text-grey">
                        {new Date(req.created_at).toLocaleString("tr-TR", { dateStyle: "short", timeStyle: "short" })}
                      </TableCell>

                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-1.5">
                          {[1, 2, 3].map((num) => (
                            <span
                              key={num}
                              className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
                                callCount >= num
                                  ? "bg-emerald-600 text-white shadow-sm"
                                  : "bg-slate-100 text-stone-400 border border-slate-200"
                              }`}
                            >
                              {num}
                            </span>
                          ))}
                        </div>
                        {req.last_call_at && (
                          <p className="text-[11px] text-stone-500 font-medium mt-1">
                            Son: {format(new Date(req.last_call_at), "dd.MM HH:mm")}
                          </p>
                        )}
                      </TableCell>

                      <TableCell>
                        <Badge
                          className={`text-xs ${
                            req.status === "pending"
                              ? "bg-amber-100 text-amber-800"
                              : req.status === "in_progress"
                              ? "bg-blue-100 text-blue-800"
                              : req.status === "unreachable"
                              ? "bg-red-100 text-red-800"
                              : req.status === "demo_scheduled"
                              ? "bg-purple-100 text-purple-800"
                              : req.status === "converted"
                              ? "bg-emerald-100 text-emerald-800"
                              : req.status === "called"
                              ? "bg-teal-100 text-teal-800"
                              : "bg-slate-100 text-stone-600"
                          }`}
                        >
                          {req.status === "pending"
                            ? "Bekliyor"
                            : req.status === "in_progress"
                            ? `Aranıyor (${callCount}/3)`
                            : req.status === "unreachable"
                            ? "Ulaşılamadı"
                            : req.status === "demo_scheduled"
                            ? "Demo Planlandı"
                            : req.status === "converted"
                            ? "Satış Yapıldı"
                            : req.status === "called"
                            ? "Görüşüldü"
                            : "İptal Edildi"}
                        </Badge>
                      </TableCell>

                      <TableCell className="max-w-[200px]">
                        <p className="text-xs text-stone-600 truncate" title={req.last_note || "-"}>
                          {req.last_note || <span className="text-stone-300">-</span>}
                        </p>
                      </TableCell>

                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          className="rounded-xl h-8 px-3 text-xs"
                        >
                          Yönet
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Card>

          <div className="md:hidden space-y-3">
            {demoRequests
              .filter((req) => {
                if (demoStatusFilter !== "all" && req.status !== demoStatusFilter) return false;
                if (!demoSearchQuery) return true;
                const q = demoSearchQuery.toLowerCase();
                return (
                  req.name?.toLowerCase().includes(q) ||
                  req.surname?.toLowerCase().includes(q) ||
                  req.phone?.includes(q)
                );
              })
              .map((req) => {
                const callCount = req.call_count || (req.call_logs ? req.call_logs.length : 0);
                return (
                  <Card
                    key={req.demo_id}
                    onClick={() => {
                      setSelectedDemoLead(req);
                      setCallLogForm({ result: "spoke", note: "" });
                      setOutcomeForm({ type: "", note: "", scheduled_date: "" });
                    }}
                    className="border-0 shadow-sm cursor-pointer p-4 space-y-3"
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <h4 className="font-bold text-navy text-base">{req.name} {req.surname}</h4>
                        <div className="flex items-center gap-1.5 mt-1">
                          {req.branch && (
                            <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-800 border-emerald-200">
                              {req.branch}
                            </Badge>
                          )}
                          <span className="text-[11px] text-grey">
                            {new Date(req.created_at).toLocaleDateString("tr-TR")}
                          </span>
                        </div>
                      </div>

                      <div className="flex items-center gap-1">
                        {[1, 2, 3].map((num) => (
                          <span
                            key={num}
                            className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${
                              callCount >= num ? "bg-emerald-600 text-white" : "bg-slate-100 text-stone-400"
                            }`}
                          >
                            {num}
                          </span>
                        ))}
                      </div>
                    </div>

                    {req.last_note && (
                      <p className="text-xs bg-slate-50 p-2.5 rounded-xl text-stone-600 italic">
                        "{req.last_note}"
                      </p>
                    )}

                    <div className="flex items-center justify-between pt-1 border-t" onClick={(e) => e.stopPropagation()}>
                      <a
                        href={getWhatsAppLink(req.phone)}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 bg-emerald-50 px-3 py-1.5 rounded-lg"
                      >
                        <MessageCircle className="w-3.5 h-3.5" /> WhatsApp'tan Yaz
                      </a>

                      <Button
                        size="sm"
                        variant="outline"
                        className="rounded-xl h-8 px-3 text-xs"
                        onClick={() => {
                          setSelectedDemoLead(req);
                          setCallLogForm({ result: "spoke", note: "" });
                          setOutcomeForm({ type: "", note: "", scheduled_date: "" });
                        }}
                      >
                        Yönet
                      </Button>
                    </div>
                  </Card>
                );
              })}
          </div>
        </div>
      )}

      {selectedMember && (
        <Dialog open={!!selectedMember} onOpenChange={() => {
          setSelectedMember(null);
          setMemberPackagesList([]);
        }}>
          <DialogContent className="max-w-2xl w-[95vw] sm:w-[600px] max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center justify-between">
                <span>{t('members:memberDetail')}</span>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="rounded-xl"
                    onClick={() => fetchMemberFullInfo(selectedMember.member_id)}
                  >
                    <Users className="w-4 h-4 mr-2" />
                    Genel Bilgiler
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="rounded-xl"
                    onClick={() => handleEditMember(selectedMember)}
                  >
                    <Edit className="w-4 h-4 mr-2" />
                    {t("common:edit")}
                  </Button>
                </div>
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-6 mt-4 min-h-[350px]">
              <div className="flex items-center gap-4">
                <Avatar className="h-16 w-16">
                  <AvatarFallback className="bg-navy text-white text-xl">
                    {selectedMember.name?.charAt(0)}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <h3 className="font-heading font-bold text-xl text-navy">
                    {selectedMember.name} {selectedMember.surname}
                  </h3>
                  {selectedMember.member_no ? (<code className="text-sm bg-slate-100 px-2 py-1 rounded">
                    {selectedMember.member_no}
                  </code>) : <span className="text-xs font-semibold bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded" data-testid="member-candidate-badge">Aday</span>}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-slate-50 rounded-xl">
                  <p className="text-sm text-grey mb-1">{t('members:registrationDate')}</p>
                  <p className="font-medium flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-navy" />
                    {selectedMember.start_date
                      ? format(new Date(selectedMember.start_date), "dd.MM.yyyy", {
                          locale: getDateLocale(),
                        })
                      : "-"}
                  </p>
                </div>
                <div className="p-4 bg-slate-50 rounded-xl">
                  <p className="text-sm text-grey mb-1">{t('members:membershipCard')}</p>
                  <p className="font-medium flex items-center gap-2">
                    <CreditCard className="w-4 h-4 text-navy" />
                    {selectedMember.member_card || selectedMember.used_card || "Yok"}
                  </p>
                </div>
              </div>

              <RestrictionPanel member={selectedMember} onChanged={patchMember} />
              <RecurringTemplateInfo member={selectedMember} onChanged={patchMember} />

              {selectedMember.health_notes && (
                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-xl">
                  <p className="text-sm font-medium text-yellow-800 mb-1 flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    {t("common:healthNotes")}
                  </p>
                  <p className="text-yellow-700">{selectedMember.health_notes}</p>
                </div>
              )}

              <div className="space-y-3">
                <h4 className="font-semibold text-navy flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Form Durumu
                </h4>
                
                {loadingMemberForms ? (
                  <div className="flex justify-center py-2">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-navy" />
                  </div>
                ) : memberFormStatus.length > 0 ? (
                  <div className="grid grid-cols-2 gap-2">
                    {memberFormStatus.map((form) => {
                      const isCompleted = form.status === "completed";
                      return (
                        <div
                          key={form.form_id}
                          className={`p-3 rounded-xl border ${
                            isCompleted
                              ? "bg-green-50 border-green-200"
                              : "bg-orange-50 border-orange-200"
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            {isCompleted ? (
                              <CheckCircle2 className="w-4 h-4 text-green-600" />
                            ) : (
                              <AlertCircle className="w-4 h-4 text-orange-600" />
                            )}
                            <span className={`text-sm font-medium ${
                              isCompleted ? "text-green-700" : "text-orange-700"
                            }`}>
                              {form.title}
                            </span>
                          </div>
                          <p className="text-xs mt-1 ml-6 text-grey">
                            {isCompleted && form.completed_at
                              ? format(new Date(form.completed_at), "d MMM yyyy", { locale: getDateLocale() })
                              : form.is_required
                              ? t("common:notFilledRequired")
                              : t("common:notFilled")}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-sm text-grey">Aktif form bulunmuyor</p>
                )}
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="font-semibold text-navy flex items-center gap-2">
                    <Package className="w-4 h-4" />
                    {t("packages:purchasedPackages")}
                  </h4>
                  <Button 
                    size="sm" 
                    variant="outline" 
                    className="rounded-xl"
                    onClick={() => openSellPackageModal(selectedMember)}
                  >
                    <Plus className="w-3 h-3 mr-1" />
                    {t("packages:sellPackage")}
                  </Button>
                </div>
                
                {loadingMemberPackages ? (
                  <div className="flex justify-center py-4">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-navy" />
                  </div>
                ) : memberPackagesList.length > 0 ? (
                  <div className="space-y-2">
                    {memberPackagesList.map((pkg) => {
                      const isExpired = pkg.end_date && new Date(pkg.end_date) < new Date();
                      const isFinished = pkg.remaining_sessions === 0;
                      const isCancelled = pkg.status === 'cancelled';
                      const isActive = pkg.is_active && !isExpired && !isFinished && !isCancelled;
                      const pkgPrice = pkg.price || pkg.sale_price || 0;
                      const paidAmount = pkg.paid_amount || 0;
                      const isFreeCancel = isCancelled && pkg.cancel_type === 'free';
                      const isRefundCancel = isCancelled && pkg.cancel_type === 'refund';
                      const remaining = isRefundCancel ? 0 : Math.max(0, pkgPrice - paidAmount);
                      
                      return (
                        <div 
                          key={pkg.member_package_id || pkg.mp_id} 
                          className={`p-3 rounded-xl border ${
                            isCancelled ? 'bg-red-50 border-red-200' :
                            isActive ? 'bg-green-50 border-green-200' : 
                            !pkg.is_active ? 'bg-slate-100 border-slate-200' :
                            'bg-orange-50 border-orange-200'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                {pkg.package_no && (
                                  <span className="font-mono text-xs text-grey-600 bg-white border border-grey-200 px-1.5 py-0.5 rounded" data-testid={`member-package-no-${pkg.mp_id || pkg.member_package_id}`}>
                                    {pkg.package_no}
                                  </span>
                                )}
                                <p className="font-medium text-navy">{pkg.package_name || 'Paket'}</p>
                                {isCancelled && (
                                  <Badge className="bg-red-100 text-red-700 text-xs">
                                    {isFreeCancel ? t('common:freeCancellation2') : t('common:refundCancellation')}
                                  </Badge>
                                )}
                                {isActive && (
                                  <Badge className="bg-green-100 text-green-700 text-xs">{t('admin:active')}</Badge>
                                )}
                                {!pkg.is_active && !isCancelled && (
                                  <Badge variant="secondary" className="text-xs">{t('common:passive')}</Badge>
                                )}
                                {isFinished && pkg.is_active && (
                                  <Badge className="bg-orange-100 text-orange-700 text-xs">Bitti</Badge>
                                )}
                                {isExpired && pkg.is_active && !isFinished && (
                                  <Badge className="bg-red-100 text-red-700 text-xs">{t('members:expired')}</Badge>
                                )}
                                {remaining > 0 ? (
                                  <Badge className="bg-red-100 text-red-700 text-xs">{formatPrice(remaining)} borç</Badge>
                                ) : pkgPrice > 0 ? (
                                  <Badge className="bg-blue-100 text-blue-700 text-xs">{t('payments:paid')}</Badge>
                                ) : null}
                              </div>
                              <div className="grid grid-cols-4 gap-2 text-sm">
                                <div>
                                  <span className="text-grey">Kalan:</span>{' '}
                                  <span className={`font-semibold ${pkg.remaining_sessions === 0 ? 'text-red-600' : 'text-navy'}`}>
                                    {pkg.remaining_sessions} / {pkg.total_sessions || '?'}
                                  </span>
                                </div>
                                <div>
                                  <span className="text-grey">Fiyat:</span>{' '}
                                  <span className="text-navy font-semibold">
                                    {formatPrice(pkgPrice)}
                                  </span>
                                </div>
                                <div>
                                  <span className="text-grey">Başlangıç:</span>{' '}
                                  <span className="text-navy">
                                    {pkg.start_date ? format(new Date(pkg.start_date), "dd/MM/yy", { locale: getDateLocale() }) : '-'}
                                  </span>
                                </div>
                                <div className="flex items-center gap-1">
                                  <div>
                                    <span className="text-grey">Bitiş:</span>{' '}
                                    <span className={isExpired ? 'text-red-600' : 'text-navy'}>
                                      {pkg.end_date ? format(new Date(pkg.end_date), "dd/MM/yy", { locale: getDateLocale() }) : '-'}
                                    </span>
                                  </div>
                                  {!isCancelled && (
                                    <button
                                      type="button"
                                      onClick={(e) => { e.stopPropagation(); openEditDates(pkg); }}
                                      className="ml-1 p-1 rounded hover:bg-white/60 text-grey hover:text-navy"
                                      title="Tarihleri düzenle"
                                      data-testid={`edit-dates-${pkg.mp_id || pkg.member_package_id}`}
                                    >
                                      <CalendarDays className="w-3.5 h-3.5" />
                                    </button>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-1 mt-1 text-sm">
                                <span className="text-grey">{t("common:trainer")}:</span>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setAssignTrainerPkg(pkg);
                                    setAssignTrainerId(pkg.trainer_id || "none");
                                  }}
                                  className="hover:underline"
                                >
                                  {pkg.trainer_name ? (
                                    <span className="text-navy font-medium">{pkg.trainer_name}</span>
                                  ) : (
                                    <span className="text-amber-600 text-xs">{t('members:assignTrainer')}</span>
                                  )}
                                </button>
                              </div>
                              {pkg.deactivate_reason && (
                                <p className="text-xs text-grey mt-1">
                                  Pasif nedeni: {pkg.deactivate_reason}
                                </p>
                              )}
                            </div>
                            <div className="flex gap-1">
                              {pkg.status !== "cancelled" && (
                                <>
                                  {pkg.is_active ? (
                                    <Button 
                                      size="sm" 
                                      variant="ghost" 
                                      className="text-orange-600 hover:bg-orange-50 text-xs px-2"
                                      onClick={() => openDeactivateModal(pkg)}
                                    >
                                      {t("packages:deactivatePackage")}
                                    </Button>
                                  ) : (
                                    <Button 
                                      size="sm" 
                                      variant="ghost" 
                                      className="text-green-600 hover:bg-green-50 text-xs px-2"
                                      onClick={() => handleReactivatePackage(pkg)}
                                    >
                                      {t("packages:activatePackage")}
                                    </Button>
                                  )}
                                  <Button 
                                    size="sm" 
                                    variant="ghost" 
                                    className="text-red-600 hover:bg-red-50 text-xs px-2"
                                    onClick={() => openCancelModal(pkg)}
                                  >
                                    {t("common:cancelIt")}
                                  </Button>
                                </>
                              )}
                              {pkg.status === "cancelled" && (
                                <Badge variant="outline" className="text-red-600 border-red-200">
                                  {t("common:cancelled2")}
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="p-4 text-center bg-slate-50 rounded-xl">
                    <Package className="w-8 h-8 text-grey mx-auto mb-2" />
                    <p className="text-grey text-sm">{t('members:noPackagePurchased')}</p>
                  </div>
                )}
              </div>

              {memberPackagesList.length > 0 && (
                <div className="p-4 bg-slate-50 rounded-xl">
                  <h4 className="font-semibold text-navy mb-3 flex items-center gap-2">
                    <CreditCard className="w-4 h-4" />
                    {t("common:paymentStatusTitle")}
                  </h4>
                  <div className="space-y-2">
                    {memberPackagesList.map((pkg) => {
                      const paid = pkg.paid_amount || 0;
                      const refunded = pkg.refunded_amount || 0;
                      const total = pkg.price || pkg.sale_price || 0;
                      const isFreeCancel = pkg.status === 'cancelled' && pkg.cancel_type === 'free';
                      const isRefundCancel = pkg.status === 'cancelled' && pkg.cancel_type === 'refund';
                      
                      if (isRefundCancel) {
                        const netPaid = Math.max(0, paid - refunded);
                        return (
                          <div key={pkg.member_package_id || pkg.mp_id} className="flex items-center justify-between text-sm">
                            <span className="text-grey line-through">{pkg.package_name}:</span>
                            <div className="flex items-center gap-2">
                              <Badge variant="secondary" className="text-xs">{t('members:refundCancelled')}</Badge>
                              <span className="text-green-600 font-semibold">{currencySymbol}0</span>
                              <span className="text-grey text-xs">
                                (Net: {formatPrice(netPaid)})
                              </span>
                            </div>
                          </div>
                        );
                      }
                      
                      if (isFreeCancel) {
                        const remaining = Math.max(0, total - paid);
                        return (
                          <div key={pkg.member_package_id || pkg.mp_id} className="flex items-center justify-between text-sm">
                            <span className="text-grey">{pkg.package_name}:</span>
                            <div className="flex items-center gap-2">
                              <Badge className="bg-red-100 text-red-700 text-xs">{t('members:freeCancellation')}</Badge>
                              {remaining > 0 ? (
                                <span className="text-red-600 font-semibold">{formatPrice(remaining)} borç</span>
                              ) : (
                                <span className="text-green-600">{t('payments:paid')}</span>
                              )}
                            </div>
                          </div>
                        );
                      }
                      
                      const remaining = Math.max(0, total - paid);
                      
                      return (
                        <div key={pkg.member_package_id || pkg.mp_id} className="flex items-center justify-between text-sm">
                          <span className="text-grey">{pkg.package_name}:</span>
                          <div className="flex items-center gap-2">
                            {remaining > 0 ? (
                              <span className="text-red-600 font-semibold">{formatPrice(remaining)} kaldı</span>
                            ) : (
                              <span className="text-green-600 font-semibold">Ödendi ✓</span>
                            )}
                            <span className="text-grey text-xs">({formatPrice(paid)} / {formatPrice(total)})</span>
                          </div>
                        </div>
                      );
                    })}
                    <div className="border-t pt-2 mt-2 flex items-center justify-between font-semibold">
                      <span>{t("common:totalDebt")}</span>
                      <span className={memberPackagesList.filter(p => p.status !== 'cancelled' || p.cancel_type === 'free').reduce((sum, p) => sum + Math.max(0, (p.price || p.sale_price || 0) - (p.paid_amount || 0)), 0) > 0 ? 'text-red-600' : 'text-green-600'}>
                        {formatPrice(memberPackagesList.filter(p => p.status !== 'cancelled' || p.cancel_type === 'free').reduce((sum, p) => sum + Math.max(0, (p.price || p.sale_price || 0) - (p.paid_amount || 0)), 0))}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex gap-3">
                <Button 
                  variant="outline" 
                  className="flex-1 rounded-xl"
                  onClick={() => openMeasurementsModal(selectedMember)}
                >
                  <Activity className="w-4 h-4 mr-2" />
                  {t("evaluations:measurements")}
                </Button>
                <Button 
                  className="flex-1 bg-navy hover:bg-navy/90 rounded-xl"
                  onClick={() => {
                    window.location.href = `/dashboard/reservations?member=${selectedMember.member_id}`;
                  }}
                >
                  <Calendar className="w-4 h-4 mr-2" />
                  Rezervasyon Yap
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
      
      <Dialog open={showDeactivateModal} onOpenChange={setShowDeactivateModal}>
        <DialogContent className="max-w-md w-[95vw] sm:w-auto">
          <DialogHeader>
            <DialogTitle>{t('members:deactivatePackage')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="p-3 bg-orange-50 rounded-xl">
              <p className="text-sm text-orange-700">
                {t("common:deactivatePackageConfirm", { name: packageToDeactivate?.package_name })}
              </p>
            </div>
            
            <div>
              <Label>{t("admin:deactivateReasonRequired")}</Label>
              <Textarea
                placeholder={t("surveys:ph_ornUyeGeciciOlarakAra")}
                value={deactivateReason}
                onChange={(e) => setDeactivateReason(e.target.value)}
                className="rounded-xl"
                required
              />
              {!deactivateReason.trim() && (
                <p className="text-xs text-red-500 mt-1">Neden zorunludur</p>
              )}
            </div>
            
            <div className="flex gap-3">
              <Button 
                variant="outline" 
                className="flex-1 rounded-xl"
                onClick={() => setShowDeactivateModal(false)}
              >
                {t("common:giveUp2")}
              </Button>
              <Button 
                className="flex-1 bg-orange-600 hover:bg-orange-700 rounded-xl"
                onClick={handleDeactivatePackage}
                disabled={!deactivateReason.trim()}
              >
                {t("packages:deactivatePackage")}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      <Dialog open={showCancelModal} onOpenChange={setShowCancelModal}>
        <DialogContent className="max-w-md w-[95vw] sm:w-auto">
          <DialogHeader>
            <DialogTitle className="text-red-600">{t('members:cancelPackage')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="p-3 bg-red-50 rounded-xl">
              <p className="text-sm text-red-700">
                {t("common:cancelPackageConfirm", { name: packageToCancel?.package_name })}
              </p>
            </div>
            
            <div>
              <Label>{t("common:cancelType")}</Label>
              <div className="flex gap-2 mt-2">
                <Button
                  type="button"
                  variant={cancelType === "free" ? "default" : "outline"}
                  className={`flex-1 rounded-xl ${cancelType === "free" ? "bg-navy" : ""}`}
                  onClick={() => setCancelType("free")}
                >
                  Ücretsiz {t("common:cancel")}
                </Button>
                <Button
                  type="button"
                  variant={cancelType === "refund" ? "default" : "outline"}
                  className={`flex-1 rounded-xl ${cancelType === "refund" ? "bg-navy" : ""}`}
                  onClick={() => setCancelType("refund")}
                >
                  İadeli {t("common:cancel")}
                </Button>
              </div>
            </div>
            
            {cancelType === "refund" && (
              <div className="space-y-3 p-3 bg-slate-50 rounded-xl">
                <div>
                  <Label>{t("common:refundAmountRequired")}</Label>
                  <Input
                    type="number"
                    value={refundAmount}
                    onChange={(e) => setRefundAmount(parseTurkishNumber(e.target.value) || 0)}
                    className="rounded-xl"
                    min={0}
                    max={packageToCancel?.paid_amount || 0}
                  />
                  <p className="text-xs text-grey mt-1">
                    {t("common:paidAmount")}: {formatPrice(packageToCancel?.paid_amount)}
                  </p>
                </div>
                
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="refundNow"
                    checked={refundNow}
                    onChange={(e) => setRefundNow(e.target.checked)}
                    className="rounded"
                  />
                  <Label htmlFor="refundNow" className="text-sm cursor-pointer">
                    {t("common:refundNow")}
                  </Label>
                </div>
                {!refundNow && (
                  <p className="text-xs text-amber-600">
                    {t("common:refundLater")}
                  </p>
                )}
              </div>
            )}
            
            <div>
              <Label>{t("common:cancelReasonRequired")}</Label>
              <Textarea
                placeholder={t("common:explainCancelReason")}
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                className="rounded-xl"
                required
              />
              {!cancelReason.trim() && (
                <p className="text-xs text-red-500 mt-1">Neden zorunludur</p>
              )}
            </div>
            
            <div className="flex gap-3">
              <Button 
                variant="outline" 
                className="flex-1 rounded-xl"
                onClick={() => setShowCancelModal(false)}
              >
                {t("common:giveUp2")}
              </Button>
              <Button 
                className="flex-1 bg-red-600 hover:bg-red-700 rounded-xl"
                onClick={handleCancelPackage}
                disabled={!cancelReason.trim() || isCancelling}
              >
                {isCancelling ? t("common:processing") : t("common:cancel")}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      <Dialog open={showSellPackageModal} onOpenChange={(open) => {
        setShowSellPackageModal(open);
        if (!open) {
          setSellPackageId("");
          setSellTrainer("");
          setSellPrice("");
          setSellMinPrice(0);
          setSellNormalPrice(0);
          setSellPaidAmount("0");
          setSellPaymentMethod("cash");
          setSellDiscountReason("");
          setSellIsTransfer(false);
          setSellTransferUsed("0");
          setSellTransferEnd("");
          setSelectedMember(null);
        }
      }}>
        <DialogContent className="max-w-lg w-[95vw] sm:w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
          <DialogHeader>
            <DialogTitle>{t('members:packageSale')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {recentSales.stats && (
              <div className="grid grid-cols-3 gap-2 p-3 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl border border-emerald-100">
                <div className="text-center">
                  <p className="text-xs text-emerald-600 font-medium">{t('common:today')}</p>
                  <p className="text-lg font-bold text-emerald-700">{recentSales.stats.today?.count || 0}</p>
                  <p className="text-xs text-emerald-600">{formatPrice((recentSales.stats.today?.total || 0))}</p>
                </div>
                <div className="text-center border-x border-emerald-200">
                  <p className="text-xs text-emerald-600 font-medium">{t('common:thisWeek')}</p>
                  <p className="text-lg font-bold text-emerald-700">{recentSales.stats.week?.count || 0}</p>
                  <p className="text-xs text-emerald-600">{formatPrice((recentSales.stats.week?.total || 0))}</p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-emerald-600 font-medium">{t('common:thisMonth')}</p>
                  <p className="text-lg font-bold text-emerald-700">{recentSales.stats.month?.count || 0}</p>
                  <p className="text-xs text-emerald-600">{formatPrice((recentSales.stats.month?.total || 0))}</p>
                </div>
              </div>
            )}
            
            <div className="p-3 bg-slate-50 rounded-xl">
              <p className="text-sm text-grey">{t("common:member")}</p>
              <p className="font-medium">{selectedMember?.name} {selectedMember?.surname}</p>
            </div>
            
            <div>
              <Label>Satış Tarihi</Label>
              <DatePickerTR
                value={sellSaleDate}
                onChange={(ds) => ds && setSellSaleDate(ds)}
                returnDateString
                placeholder="Tarih seç"
                data-testid="sell-package-sale-date-members"
              />
              <p className="text-xs text-grey mt-1">Geriye dönük tarih de seçebilirsiniz.</p>
            </div>
            
            {canTransferPackage && (
              <TransferPackageFields
                enabled={sellIsTransfer}
                onToggle={setSellIsTransfer}
                usedSessions={sellTransferUsed}
                onUsedChange={setSellTransferUsed}
                endDate={sellTransferEnd}
                onEndDateChange={setSellTransferEnd}
                totalSessions={packages.find(p => p.package_id === sellPackageId)?.session_count || 0}
                testIdSuffix="-members"
              />
            )}
            
            <div>
              <Label>{t('members:selectPackage')}</Label>
              {packagesWithPrice.length > 0 ? (
                <>
                  <Select value={sellPackageId} onValueChange={(v) => {
                    setSellPackageId(v);
                    let price = 0;
                    let discountedPrice = 0;
                    if (activePriceList?.prices) {
                      const priceItem = activePriceList.prices.find(p => p.package_id === v);
                      if (priceItem) {
                        price = priceItem.price || 0;
                        discountedPrice = priceItem.discounted_price || 0;
                      }
                    }
                    setSellPrice(price.toString());
                    setSellNormalPrice(price);
                    setSellMinPrice(discountedPrice > 0 ? discountedPrice : 0);
                    setSellDiscountReason("");
                    setSellTrainer("none");
                  }}>
                    <SelectTrigger className="rounded-xl">
                      <SelectValue placeholder={t("packages:ph_paketSecin")} />
                    </SelectTrigger>
                    <SelectContent>
                      {packagesWithPrice.map((pkg) => {
                        const priceItem = activePriceList?.prices?.find(p => p.package_id === pkg.package_id);
                        const displayPrice = priceItem?.price || 0;
                        const displayDiscounted = priceItem?.discounted_price || 0;
                        return (
                          <SelectItem key={pkg.package_id} value={pkg.package_id}>
                            {pkg.name} - {pkg.session_count} ders - {formatPrice(displayPrice)}
                            {displayDiscounted > 0 && (
                              <span className="text-green-600 text-xs ml-1">(min: {formatPrice(displayDiscounted)})</span>
                            )}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                  {activePriceList && (
                    <p className="text-xs text-green-600 mt-1">
                      {t("common:prices")}: {activePriceList.name}
                    </p>
                  )}
                </>
              ) : (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-800">
                  {t("packages:priceListRequired")}
                </div>
              )}
            </div>
            
            <div>
              <Label>{t("common:salePrice")} ({currencySymbol})</Label>
              {(() => {
                const isOwner = user?.user_id === businessOwnerId;
                const isManager = currentUserTrainer?.is_manager === true;
                const canFullDiscount = isOwner || isManager;
                
                const minAllowed = canFullDiscount ? 0 : (sellMinPrice > 0 ? sellMinPrice : sellNormalPrice);
                const maxAllowed = canFullDiscount ? Infinity : sellNormalPrice;
                
                return (
                  <>
                    <Input
                      type="number"
                      value={sellPrice}
                      onChange={(e) => {
                        const inputValue = e.target.value;
                        if (inputValue === "" || inputValue === null) {
                          setSellPrice("");
                          return;
                        }
                        setSellPrice(inputValue);
                      }}
                      onBlur={(e) => {
                        const finalPrice = parseTurkishNumber(e.target.value) || 0;
                        if (finalPrice > 0) {
                          if (finalPrice < minAllowed) {
                            toast.error(`${t("settings:priceLabel")} min: ${currencySymbol}${minAllowed.toLocaleString()}`);
                            setSellPrice(String(minAllowed));
                          } else if (!canFullDiscount && finalPrice > maxAllowed) {
                            toast.error(`${t("settings:priceLabel")} max: ${currencySymbol}${maxAllowed.toLocaleString()}`);
                            setSellPrice(String(sellNormalPrice));
                          }
                        }
                      }}
                      className="rounded-xl"
                      placeholder={t("reports:normalPriceComesHere")}
                    />
                    {!canFullDiscount && sellMinPrice > 0 && (
                      <p className="text-xs text-blue-600 mt-1">
                        💡 Min: {currencySymbol}{sellMinPrice.toLocaleString('tr-TR')} - Max: {currencySymbol}{sellNormalPrice.toLocaleString('tr-TR')}
                      </p>
                    )}
                    {!canFullDiscount && sellMinPrice === 0 && sellNormalPrice > 0 && (
                      <p className="text-xs text-amber-600 mt-1">
                        ⚠️ {t("common:noDiscountPermission", { price: sellNormalPrice.toLocaleString('tr-TR') })}
                      </p>
                    )}
                    {canFullDiscount && sellMinPrice > 0 && (
                      <p className="text-xs text-green-600 mt-1">
                        ✓ {t("common:adminCanSellBelowMin", { price: sellMinPrice.toLocaleString('tr-TR') })}
                      </p>
                    )}
                  </>
                );
              })()}
            </div>
            
            {!sellIsTransfer && sellNormalPrice > 0 && parseInt(sellPrice) < sellNormalPrice && (
              <div>
                <Label className="text-amber-600">{t("common:discountReasonRequired")}</Label>
                <Textarea
                  value={sellDiscountReason}
                  onChange={(e) => setSellDiscountReason(e.target.value)}
                  className="rounded-xl"
                  placeholder={t("common:ph_nedenIndirimYapiliyorOrnKampanya")}
                  rows={2}
                />
                <p className="text-xs text-amber-600 mt-1">
                  {t("common:discountReasonNote")}
                </p>
              </div>
            )}
            
            <div>
              <Label>{sellIsTransfer ? "Daha önce ödenen tutar" : t("common:paidAmountLabel")} ({currencySymbol})</Label>
              <Input
                type="number"
                value={sellPaidAmount}
                onChange={(e) => setSellPaidAmount(e.target.value)}
                className="rounded-xl"
                placeholder="0"
                data-testid="sell-paid-amount-members"
              />
              <p className="text-xs text-grey mt-1">{sellIsTransfer ? "Gelir raporlarına girmez; kalan tutar borç olarak görünür." : t("common:leaveZeroIfNoPayment")}</p>
            </div>
            
            {!sellIsTransfer && parseInt(sellPaidAmount || 0) > 0 && (
              <div>
                <Label>{t('payments:paymentMethod')}</Label>
                <Select value={sellPaymentMethod} onValueChange={setSellPaymentMethod}>
                  <SelectTrigger className="rounded-xl">
                    <SelectValue placeholder={t("common:selectPaymentMethod")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">{t('payments:cash')}</SelectItem>
                    <SelectItem value="credit_card">{t('payments:creditCard')}</SelectItem>
                    <SelectItem value="bank_transfer">{t('payments:bankTransfer')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            
            <div>
              <Label>{t("members:trainerOptional")}</Label>
              <Select value={sellTrainer} onValueChange={setSellTrainer}>
                <SelectTrigger className="rounded-xl">
                  <SelectValue placeholder={t("common:selectTrainer")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t('trainers:noTrainer')}</SelectItem>
                  {(() => {
                    const selectedPkg = packages.find(p => p.package_id === sellPackageId);
                    const pkgBranches = selectedPkg?.branches || [];
                    return trainers
                      .filter(t => t.is_active !== false)
                      .filter(t => {
                        if (pkgBranches.length === 0) return true;
                        const trainerBranches = t.branches || [];
                        return pkgBranches.some(b => trainerBranches.includes(b));
                      })
                      .map((trainer) => (
                        <SelectItem key={trainer.trainer_id} value={trainer.trainer_id}>
                          {trainer.name} {trainer.surname}
                        </SelectItem>
                      ));
                  })()}
                </SelectContent>
              </Select>
            </div>
            
            <Button
              className="w-full bg-navy hover:bg-navy/90 rounded-xl"
              onClick={handleSellPackage}
              disabled={!sellPackageId || isSellingPackage}
              data-testid="sell-package-submit-btn"
            >
              {isSellingPackage ? t("common:saving") : t("packages:sellPackage")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
      <Dialog open={showEditModal} onOpenChange={(open) => {
        setShowEditModal(open);
        if (!open) setEditingMember(null);
      }}>
        <DialogContent className="max-w-lg w-[95vw] sm:w-full max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('members:editMember')}</DialogTitle>
          </DialogHeader>
          {editingMember && (
            <div className="space-y-4 mt-4">
              <div className="p-3 bg-slate-50 rounded-xl">
                <p className="font-medium">{editingMember.name} {editingMember.surname}</p>
                <code className="text-xs bg-slate-100 px-1.5 py-0.5 rounded">{editingMember.member_no}</code>
                <span className="text-xs text-grey ml-2">{editingMember.email}</span>
                {editingMember.is_app_registered === false && (
                  <span className="ml-2 text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">{t('members:unregistered')}</span>
                )}
              </div>
              
              {editingMember.is_app_registered === false && (
                <>
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-xl">
                    <p className="text-xs text-blue-700">
                      {t("admin:unregisteredMemberNote")}
                    </p>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>{t("common:firstName")} <span className="text-red-500">*</span></Label>
                      <Input
                        value={editingMember.name || ""}
                        onChange={(e) => setEditingMember({...editingMember, name: e.target.value})}
                        className="rounded-xl"
                        autoComplete="off"
                      />
                    </div>
                    <div>
                      <Label>{t("common:surname")} <span className="text-red-500">*</span></Label>
                      <Input
                        value={editingMember.surname || ""}
                        onChange={(e) => setEditingMember({...editingMember, surname: e.target.value})}
                        className="rounded-xl"
                        autoComplete="off"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>{t("common:phone")} <span className="text-red-500">*</span></Label>
                      <Input
                        value={editingMember.phone || ""}
                        onChange={(e) => setEditingMember({...editingMember, phone: e.target.value})}
                        className="rounded-xl"
                        placeholder="05XX XXX XX XX"
                        autoComplete="off"
                      />
                    </div>
                    <div>
                      <Label>{t('common:email')}</Label>
                      <Input
                        type="email"
                        value={editingMember.email || ""}
                        onChange={(e) => setEditingMember({...editingMember, email: e.target.value})}
                        className="rounded-xl"
                        placeholder="ornek@email.com"
                        autoComplete="off"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 gap-3">
                    <div>
                      <Label>{t("common:birthDate")} <span className="text-red-500">*</span></Label>
                      <Popover open={editBirthDateOpen} onOpenChange={setEditBirthDateOpen}>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className={cn(
                              "w-full justify-start text-left font-normal rounded-xl mt-1",
                              !editingMember.birth_date && "text-muted-foreground"
                            )}
                            data-testid="edit-birth-date-trigger"
                          >
                            <Calendar className="mr-2 h-4 w-4" />
                            {editingMember.birth_date ? (
                              format(new Date(editingMember.birth_date), "dd.MM.yyyy", { locale: getDateLocale() })
                            ) : (
                              <span>{t('common:selectDate')}</span>
                            )}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <CalendarComponent
                            mode="single"
                            selected={editingMember.birth_date ? new Date(editingMember.birth_date) : undefined}
                            onSelect={(date) => {
                              if (date) {
                                setEditingMember({
                                  ...editingMember,
                                  birth_date: format(date, "yyyy-MM-dd")
                                });
                                setEditBirthDateOpen(false);
                              }
                            }}
                            defaultMonth={editingMember.birth_date ? new Date(editingMember.birth_date) : new Date()}
                            fromYear={1940}
                            toYear={new Date().getFullYear()}
                            initialFocus={false}
                            locale={tr}
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                    <div>
                      <Label>{t("common:gender")} <span className="text-red-500">*</span></Label>
                      <Select 
                        value={editingMember.gender || ""} 
                        onValueChange={(v) => setEditingMember({...editingMember, gender: v})}
                      >
                        <SelectTrigger className="rounded-xl">
                          <SelectValue placeholder={t("common:ph_secin")} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="male">{t('common:male')}</SelectItem>
                          <SelectItem value="female">{t('common:female')}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </>
              )}
              
              <div>
                <Label className="block mb-1.5">{t('members:membershipCard')} (Çoklu Seçilebilir)</Label>
                <div className="flex flex-wrap gap-2 p-2 bg-slate-50 border rounded-xl">
                  {memberCards.map((card) => {
                    const cardName = typeof card === 'string' ? card : card.name;
                    const selectedList = editingMember.member_cards || (editingMember.member_card ? [editingMember.member_card] : []);
                    const isSelected = selectedList.includes(cardName);
                    return (
                      <button
                        key={cardName}
                        type="button"
                        onClick={() => {
                          const updated = isSelected 
                            ? selectedList.filter(c => c !== cardName) 
                            : [...selectedList, cardName];
                          setEditingMember({
                            ...editingMember,
                            member_cards: updated,
                            member_card: updated[0] || null
                          });
                        }}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                          isSelected 
                            ? "bg-navy text-white border-navy" 
                            : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"
                        }`}
                      >
                        {cardName} {isSelected && "✓"}
                      </button>
                    );
                  })}
                </div>
              </div>
              
              <div>
                <Label>{t("common:healthNotes")}</Label>
                <Textarea
                  placeholder={t("common:ph_saglikDurumuNotlar")}
                  value={editingMember.health_notes || ""}
                  onChange={(e) => setEditingMember({...editingMember, health_notes: e.target.value})}
                  className="rounded-xl"
                  rows={3}
                />
              </div>
              
              <div>
                <Label>{t("common:assignedTrainer")}</Label>
                <Select 
                  value={editingMember.trainer_id || "none"} 
                  onValueChange={(v) => setEditingMember({...editingMember, trainer_id: v === "none" ? null : v})}
                >
                  <SelectTrigger className="rounded-xl">
                    <SelectValue placeholder={t("common:selectTrainer")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{t('trainers:noTrainer')}</SelectItem>
                    {trainers.filter(t => t.is_active !== false).map((trainer) => (
                      <SelectItem key={trainer.trainer_id} value={trainer.trainer_id}>
                        {trainer.name} {trainer.surname}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-grey mt-1">
                  {t("members:trainerInfoHint")}
                </p>
              </div>
              
              {editingMember.is_app_registered !== false && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl">
                <p className="text-xs text-amber-700">
                  {t("admin:registeredMemberEditNote")}
                </p>
              </div>
              )}
              
              <div className="flex gap-3 pt-4">
                <Button 
                  variant="outline" 
                  className="flex-1 rounded-xl" 
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingMember(null);
                  }}
                >
                  {t("common:cancel")}
                </Button>
                <Button 
                  className="flex-1 bg-navy hover:bg-navy/90 rounded-xl" 
                  onClick={handleUpdateMember}
                  disabled={isSavingEdit}
                >
                  {isSavingEdit ? "Kaydediliyor..." : t('common:save')}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      <Dialog open={!!deleteMemberId} onOpenChange={(open) => !open && setDeleteMemberId(null)}>
        <DialogContent className="max-w-sm w-[95vw] sm:w-auto">
          <DialogHeader>
            <DialogTitle>{t("common:uyeyiSil")}</DialogTitle>
            <DialogDescription>
              {t("common:deleteMemberConfirm")}
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={deleteReason}
            onChange={(e) => setDeleteReason(e.target.value)}
            placeholder="Silme nedeni (zorunlu)"
            className="rounded-xl"
            rows={2}
            data-testid="member-delete-reason-input"
          />
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setDeleteMemberId(null)}
              className="rounded-xl"
              disabled={isDeleting}
            >
              {t("common:cancel")}
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDeleteMember}
              className="rounded-xl"
              disabled={isDeleting || !deleteReason.trim()}
              data-testid="member-delete-confirm-btn"
            >
              {isDeleting ? "Siliniyor..." : t('common:delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      <Dialog open={showStatusModal} onOpenChange={(open) => {
        if (!open) {
          setShowStatusModal(false);
          setStatusChangeMember(null);
        }
      }}>
        <DialogContent className="max-w-sm w-[95vw] sm:w-auto">
          <DialogHeader>
            <DialogTitle>
              {statusChangeMember?.status === "inactive" ? t('members:activateMember') : t('members:deactivateMember')}
            </DialogTitle>
            <DialogDescription>
              {statusChangeMember?.status === "inactive" 
                ? t('common:activateMemberConfirm', { name: `${statusChangeMember?.name} ${statusChangeMember?.surname}` })
                : t('common:deactivateMemberConfirmMsg', { name: `${statusChangeMember?.name} ${statusChangeMember?.surname}` })
              }
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => {
                setShowStatusModal(false);
                setStatusChangeMember(null);
              }}
              className="rounded-xl"
            >
              {t("common:cancel")}
            </Button>
            <Button
              className={statusChangeMember?.status === "inactive" 
                ? "bg-green-600 hover:bg-green-700 rounded-xl" 
                : "bg-amber-600 hover:bg-amber-700 rounded-xl"}
              onClick={confirmToggleStatus}
            >
              {statusChangeMember?.status === "inactive" ? t("admin:makeActive") : t("common:makePassive2")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showMeasurementsModal} onOpenChange={setShowMeasurementsModal}>
        <DialogContent className="max-w-lg w-[95vw] sm:w-auto max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Activity className="w-5 h-5 text-navy" />
              {t("common:measurementsFor", { name: `${selectedMember?.name} ${selectedMember?.surname}` })}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="p-4 bg-slate-50 rounded-xl space-y-3">
              <h4 className="font-medium text-navy">{t('members:addNewMeasurement')}</h4>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>{t("common:parameter")} *</Label>
                  <Select
                    value={newMeasurement.parameter}
                    onValueChange={(val) => setNewMeasurement({ ...newMeasurement, parameter: val })}
                  >
                    <SelectTrigger className="rounded-xl">
                      <SelectValue placeholder={t("common:ph_parametreSecin")} />
                    </SelectTrigger>
                    <SelectContent>
                      {measurementParameters.length > 0 ? (
                        measurementParameters.map((param) => (
                          <SelectItem key={param.name} value={param.name}>
                            {getLocalizedMeasurement(param.name)} {param.unit ? `(${param.unit})` : ''}
                          </SelectItem>
                        ))
                      ) : (
                        <SelectItem value="none" disabled>{t("admin:noActiveParamsInBusiness")}</SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                  {measurementParameters.length === 0 && (
                    <p className="text-xs text-orange-600 mt-1">{t("evaluations:ayarlarSayfasindanOlcumParametreleriEkle")}</p>
                  )}
                </div>
                <div>
                  <Label>
                    Değer {newMeasurement.parameter && measurementParameters.find(p => p.name === newMeasurement.parameter)?.unit 
                      ? `(${measurementParameters.find(p => p.name === newMeasurement.parameter).unit})` 
                      : ''} *
                  </Label>
                  <Input
                    type="number"
                    step="0.1"
                    placeholder={t("common:ph_orn755")}
                    value={newMeasurement.value}
                    onChange={(e) => setNewMeasurement({ ...newMeasurement, value: e.target.value })}
                    className="rounded-xl"
                  />
                </div>
              </div>
              <Button
                onClick={handleAddMeasurement}
                className="w-full bg-navy hover:bg-navy/90 rounded-xl"
                disabled={!newMeasurement.parameter || !newMeasurement.value || measurementParameters.length === 0}
              >
                <Plus className="w-4 h-4 mr-2" />
                {t("evaluations:addMeasurement")}
              </Button>
            </div>

            <div>
              <h4 className="font-medium text-navy mb-3">{t("evaluations:gecmisOlcumler")}</h4>
              {loadingMeasurements ? (
                <div className="text-center py-8 text-grey">{t('common:loading')}</div>
              ) : measurements.length === 0 ? (
                <div className="text-center py-8 text-grey">
                  <Activity className="w-10 h-10 mx-auto mb-2 opacity-50" />
                  <p>{t("evaluations:noMeasurementsYet")}</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-[300px] overflow-y-auto">
                  {measurements.map((m, idx) => {
                    let dateStr = "-";
                    try {
                      const dateField = m.measured_at || m.date;
                      if (dateField) {
                        const date = new Date(dateField);
                        if (!isNaN(date.getTime())) {
                          dateStr = format(date, "d MMMM yyyy, HH:mm", { locale: getDateLocale() });
                        }
                      }
                    } catch (e) {
                      dateStr = m.date || "-";
                    }
                    return (
                      <div key={m.measurement_id || idx} className="flex items-center justify-between p-3 bg-white border rounded-xl group">
                        <div>
                          <p className="font-medium text-navy">
                            {m.parameter} {m.unit ? `(${m.unit})` : ''}
                          </p>
                          <p className="text-xs text-grey">{dateStr}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <p className="text-lg font-bold text-navy">
                            {m.value} {m.unit || ''}
                          </p>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="opacity-0 group-hover:opacity-100 text-red-500 hover:bg-red-50 h-8 w-8"
                            onClick={() => handleDeleteMeasurement(m.measurement_id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showRecentSalesModal} onOpenChange={setShowRecentSalesModal}>
        <DialogContent className="max-w-4xl w-[95vw] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-emerald-600" />
              {t("members:recentSalesReport")}
            </DialogTitle>
            <DialogDescription>
              {t("common:last30DaysSalesSummary")}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 mt-4">
            {recentSales.stats && (
              <div className="grid grid-cols-3 gap-4">
                <Card className="bg-gradient-to-br from-emerald-50 to-teal-50 border-emerald-200">
                  <CardContent className="p-4 text-center">
                    <p className="text-sm text-emerald-600 font-medium">{t('common:today')}</p>
                    <p className="text-2xl font-bold text-emerald-700">{recentSales.stats.today?.count || 0}</p>
                    <p className="text-lg font-semibold text-emerald-600">{formatPrice((recentSales.stats.today?.total || 0))}</p>
                    <p className="text-xs text-emerald-500">Tahsil: {formatPrice((recentSales.stats.today?.collected || 0))}</p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
                  <CardContent className="p-4 text-center">
                    <p className="text-sm text-blue-600 font-medium">{t('common:thisWeek')}</p>
                    <p className="text-2xl font-bold text-blue-700">{recentSales.stats.week?.count || 0}</p>
                    <p className="text-lg font-semibold text-blue-600">{formatPrice((recentSales.stats.week?.total || 0))}</p>
                    <p className="text-xs text-blue-500">Tahsil: {formatPrice((recentSales.stats.week?.collected || 0))}</p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-purple-50 to-pink-50 border-purple-200">
                  <CardContent className="p-4 text-center">
                    <p className="text-sm text-purple-600 font-medium">{t('common:thisMonth')}</p>
                    <p className="text-2xl font-bold text-purple-700">{recentSales.stats.month?.count || 0}</p>
                    <p className="text-lg font-semibold text-purple-600">{formatPrice((recentSales.stats.month?.total || 0))}</p>
                    <p className="text-xs text-purple-500">Tahsil: {formatPrice((recentSales.stats.month?.collected || 0))}</p>
                  </CardContent>
                </Card>
              </div>
            )}

            <div>
              <h3 className="font-semibold text-navy mb-3">{t("common:satisDetaylari")}</h3>
              {loadingRecentSales ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-navy"></div>
                </div>
              ) : recentSales.sales?.length > 0 ? (
                <div className="border rounded-xl overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead>{t('common:date')}</TableHead>
                        <TableHead>{t("common:member")}</TableHead>
                        <TableHead>{t("packages:package")}</TableHead>
                        <TableHead className="text-right">{t('common:price')}</TableHead>
                        <TableHead className="text-right">{t("common:paidAmount")}</TableHead>
                        <TableHead className="text-right">{t('packages:remaining')}</TableHead>
                        <TableHead>{t('common:status')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {recentSales.sales.map((sale, idx) => {
                        const price = sale.price || 0;
                        const paid = sale.paid_amount || 0;
                        const refunded = sale.refunded_amount || 0;
                        const remaining = Math.max(0, price - paid);
                        const isCancelled = sale.status === "cancelled";
                        const cancelType = sale.cancel_type;
                        
                        return (
                          <TableRow key={sale.member_package_id || idx} className={isCancelled ? "bg-red-50/50" : ""}>
                            <TableCell className="text-sm">
                              {sale.created_at ? format(new Date(sale.created_at), "dd MMM yyyy", { locale: getDateLocale() }) : "-"}
                            </TableCell>
                            <TableCell className="font-medium">{sale.member_name || "-"}</TableCell>
                            <TableCell>{sale.package_name || "-"}</TableCell>
                            <TableCell className="text-right">
                              {isCancelled ? (
                                <span className="line-through text-grey">{formatPrice(price)}</span>
                              ) : (
                                <span>{formatPrice(price)}</span>
                              )}
                            </TableCell>
                            <TableCell className="text-right text-emerald-600">{formatPrice(paid)}</TableCell>
                            <TableCell className="text-right">
                              {isCancelled ? (
                                refunded > 0 ? (
                                  <span className="text-red-600">-{formatPrice(refunded)} iade</span>
                                ) : (
                                  <span className="text-grey">-</span>
                                )
                              ) : remaining > 0 ? (
                                <span className="text-amber-600">{formatPrice(remaining)}</span>
                              ) : (
                                <span className="text-emerald-600">{t('common:completed')}</span>
                              )}
                            </TableCell>
                            <TableCell>
                              {isCancelled ? (
                                <Badge variant="destructive" className="text-xs">
                                  {cancelType === "refund" ? t("common:refundCancellation") : t('reservations:freeCancellation')}
                                </Badge>
                              ) : (
                                <Badge variant="default" className="bg-emerald-100 text-emerald-700 text-xs">
                                  Aktif
                                </Badge>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="text-center py-8 text-grey">
                  <Package className="w-12 h-12 mx-auto mb-2 text-grey-300" />
                  <p>{t("common:noSalesLast30Days")}</p>
                </div>
              )}
            </div>

            <div className="bg-slate-50 rounded-xl p-4">
              <h4 className="text-sm font-medium text-navy mb-2">{t("common:hesaplamaAciklamasi")}</h4>
              <ul className="text-xs text-grey space-y-1">
                <li>• <span className="font-medium">Aktif Satış:</span> Tam fiyat satışa dahil edilir</li>
                <li>• {t("packages:freeCancelNote")}</li>
                <li>• {t("common:refundCancelNote")}</li>
              </ul>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showMemberInfoModal} onOpenChange={setShowMemberInfoModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-navy" />
              {t("common:memberGeneralInfo")}
            </DialogTitle>
          </DialogHeader>
          {memberFullInfo && (
            <div className="space-y-4 mt-4">
              <div className="flex items-center gap-4">
                <Avatar className="h-16 w-16">
                  {memberFullInfo.picture ? (
                    <AvatarImage src={memberFullInfo.picture} />
                  ) : (
                    <AvatarFallback className="bg-navy text-white text-xl">
                      {memberFullInfo.name?.charAt(0)}
                    </AvatarFallback>
                  )}
                </Avatar>
                <div>
                  <h3 className="font-heading font-bold text-xl text-navy">
                    {memberFullInfo.name} {memberFullInfo.surname}
                  </h3>
                  <p className="text-sm text-grey">{memberFullInfo.email}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-slate-50 rounded-xl">
                  <p className="text-xs text-grey mb-1">{t('members:birthDate')}</p>
                  <p className="font-medium">
                    {memberFullInfo.birth_date 
                      ? format(new Date(memberFullInfo.birth_date), "dd MMMM yyyy", { locale: getDateLocale() })
                      : t("common:notSpecified")}
                  </p>
                </div>
                <div className="p-3 bg-slate-50 rounded-xl">
                  <p className="text-xs text-grey mb-1">{t('common:gender')}</p>
                  <p className="font-medium">
                    {memberFullInfo.gender === "male" ? t('members:male') 
                      : memberFullInfo.gender === "female" ? t("common:female") 
                      : t("common:notSpecified")}
                  </p>
                </div>
                <div className="p-3 bg-slate-50 rounded-xl">
                  <p className="text-xs text-grey mb-1">{t('common:phone')}</p>
                  <p className="font-medium">{memberFullInfo.phone || t("common:notSpecified")}</p>
                </div>
                <div className="p-3 bg-slate-50 rounded-xl">
                  <p className="text-xs text-grey mb-1">{t('members:registrationDate')}</p>
                  <p className="font-medium">
                    {memberFullInfo.created_at 
                      ? format(new Date(memberFullInfo.created_at), "dd MMM yyyy", { locale: getDateLocale() })
                      : "-"}
                  </p>
                </div>
              </div>

              {memberFullInfo.bio && (
                <div className="p-3 bg-blue-50 rounded-xl">
                  <p className="text-xs text-blue-600 mb-1">{t('business:about')}</p>
                  <p className="text-sm text-blue-800">{memberFullInfo.bio}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!assignTrainerPkg} onOpenChange={(open) => !open && setAssignTrainerPkg(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{t("common:changeAssignTrainer")}</DialogTitle>
          </DialogHeader>
          {assignTrainerPkg && (
            <div className="space-y-4 mt-2">
              <div className="p-3 bg-slate-50 rounded-xl text-sm">
                <p className="font-medium text-navy">{assignTrainerPkg.package_name}</p>
              </div>
              <div>
                <Label>{t('common:trainer')}</Label>
                <Select value={assignTrainerId} onValueChange={setAssignTrainerId}>
                  <SelectTrigger className="rounded-xl" data-testid="member-assign-trainer-select">
                    <SelectValue placeholder={t("common:selectTrainer")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{t('trainers:noTrainer')}</SelectItem>
                    {trainers
                      .filter(t => t.is_active !== false)
                      .filter(t => {
                        const pkgBranches = assignTrainerPkg.package_branches || [];
                        if (pkgBranches.length === 0) return true;
                        const trainerBranches = t.branches || [];
                        return pkgBranches.some(b => trainerBranches.includes(b));
                      })
                      .map((t) => (
                        <SelectItem key={t.trainer_id} value={t.trainer_id}>
                          {t.name} {t.surname}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setAssignTrainerPkg(null)} className="rounded-xl">
              {t("common:cancel")}
            </Button>
            <Button
              onClick={handleAssignTrainerToPackage}
              disabled={isAssigningTrainer}
              className="bg-navy hover:bg-navy/90 rounded-xl"
              data-testid="member-confirm-assign-trainer-btn"
            >
              {isAssigningTrainer ? "Kaydediliyor..." : t('common:save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editingDatesPkg} onOpenChange={(open) => !open && setEditingDatesPkg(null)}>
        <DialogContent className="max-w-sm" data-testid="edit-package-dates-modal">
          <DialogHeader>
            <DialogTitle>{t("members:editPackageDates", "Paket Tarihlerini Düzenle")}</DialogTitle>
            <DialogDescription>
              {t("members:editPackageDatesHint", "Başlangıç tarihini değiştirdiğinizde bitiş tarihi paket geçerlilik süresine göre otomatik hesaplanır. Dilerseniz bitişi de manuel düzenleyebilirsiniz.")}
            </DialogDescription>
          </DialogHeader>
          {editingDatesPkg && (
            <div className="space-y-3 mt-2">
              <div>
                <Label className="text-xs">{t("common:startDate", "Başlangıç")}</Label>
                <DateInputTR value={editingDatesPkg.start_date || ""}
          onChange={(e) => handleEditStartDate(e.target.value)}
          className="rounded-xl"
          data-testid="edit-package-start-date" />
              </div>
              <div>
                <Label className="text-xs">{t("common:endDate", "Bitiş")}</Label>
                <DateInputTR value={editingDatesPkg.end_date || ""}
          onChange={(e) => setEditingDatesPkg({ ...editingDatesPkg, end_date: e.target.value })}
          className="rounded-xl"
          data-testid="edit-package-end-date" />
                <p className="text-[11px] text-grey mt-1">
                  {t("members:autoCalcNote", "Geçerlilik süresi: {{n}} gün", { n: editingDatesPkg.validity_days })}
                </p>
              </div>
            </div>
          )}
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setEditingDatesPkg(null)} className="rounded-xl">
              {t("common:cancel")}
            </Button>
            <Button
              onClick={saveEditDates}
              disabled={savingDates}
              className="bg-navy hover:bg-navy/90 rounded-xl"
              data-testid="save-package-dates-btn"
            >
              {savingDates ? t("common:saving", "Kaydediliyor...") : t("common:save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!selectedDemoLead} onOpenChange={(open) => !open && setSelectedDemoLead(null)}>
        <DialogContent className="max-w-xl w-[94vw] max-h-[90vh] overflow-y-auto rounded-3xl p-5 sm:p-6 pb-6 pt-8 sm:pt-6">
          <DialogHeader className="pt-2">
            <div className="flex items-center justify-between pr-6 border-b pb-3">
              <div>
                <DialogTitle className="text-lg font-bold text-navy flex items-center gap-2">
                  {selectedDemoLead?.name} {selectedDemoLead?.surname}
                  {selectedDemoLead?.branch && (
                    <Badge className="bg-emerald-100 text-emerald-800">{selectedDemoLead.branch}</Badge>
                  )}
                </DialogTitle>
                <div className="flex items-center gap-3 mt-1.5">
                  <a
                    href={getWhatsAppLink(selectedDemoLead?.phone)}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 bg-emerald-50 px-2 py-1 rounded-md hover:bg-emerald-100"
                  >
                    <MessageCircle className="w-3.5 h-3.5" /> WhatsApp Aç
                  </a>
                  <span className="text-xs text-stone-600 font-medium">
                    {selectedDemoLead?.phone}
                  </span>
                </div>
              </div>
            </div>
          </DialogHeader>

          {selectedDemoLead && (
            <div className="space-y-5 mt-4">
              {["converted", "demo_scheduled", "cancelled", "unreachable"].includes(selectedDemoLead.status) && (
                <div className="p-4 bg-slate-100 rounded-2xl text-center space-y-1">
                  <p className="text-sm font-bold text-navy">Bu talep sonuçlandırılmıştır.</p>
                  <p className="text-xs text-stone-600">
                    Durum: <b className="text-stone-800">{STATUS_TR[selectedDemoLead.status] || selectedDemoLead.status}</b> 
                    {" · "} 
                    Son Not: <b>{selectedDemoLead.last_note || selectedDemoLead.outcome?.note || "-"}</b>
                  </p>
                </div>
              )}

              {!["converted", "demo_scheduled", "cancelled", "unreachable"].includes(selectedDemoLead.status) && (
                <div className="p-4 bg-slate-50 border rounded-2xl space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="font-semibold text-sm text-navy flex items-center gap-2">
                      <Phone className="w-4 h-4 text-emerald-600" /> Arama Girişi
                    </h4>
                    <span className="text-xs font-bold text-stone-500">
                      Arama Hakkı: {(selectedDemoLead.call_logs?.length || 0)} / 3
                    </span>
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      type="button"
                      variant={callLogForm.result === "spoke" ? "default" : "outline"}
                      onClick={() => {
                        setCallLogForm({ ...callLogForm, result: "spoke" });
                        setOutcomeForm({ ...outcomeForm, type: "callback" });
                      }}
                      className={`rounded-xl text-xs h-10 ${
                        callLogForm.result === "spoke" ? "bg-emerald-600 text-white" : ""
                      }`}
                    >
                      ✓ Ulaşıldı (Görüşüldü)
                    </Button>
                    <Button
                      type="button"
                      variant={callLogForm.result === "no_answer" ? "default" : "outline"}
                      onClick={() => {
                        setCallLogForm({ ...callLogForm, result: "no_answer" });
                        setOutcomeForm({ ...outcomeForm, type: "" });
                      }}
                      className={`rounded-xl text-xs h-10 ${
                        callLogForm.result === "no_answer" ? "bg-red-600 text-white" : ""
                      }`}
                    >
                      ✕ Ulaşılamadı (Açmadı)
                    </Button>
                  </div>

                  {callLogForm.result === "spoke" && (
                    <div className="p-3 bg-white border border-emerald-200 rounded-xl space-y-3 animate-in fade-in">
                      <Label className="text-xs font-bold text-navy">Görüşme Sonucu Ne Oldu? *</Label>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                        <Button
                          type="button"
                          size="sm"
                          variant={outcomeForm.type === "demo_scheduled" ? "default" : "outline"}
                          onClick={() => setOutcomeForm({ ...outcomeForm, type: "demo_scheduled" })}
                          className={`rounded-xl text-xs ${outcomeForm.type === "demo_scheduled" ? "bg-purple-600 text-white" : "border-purple-200 text-purple-700"}`}
                        >
                          📅 Demo Planla
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant={outcomeForm.type === "converted" ? "default" : "outline"}
                          onClick={() => setOutcomeForm({ ...outcomeForm, type: "converted" })}
                          className={`rounded-xl text-xs ${outcomeForm.type === "converted" ? "bg-emerald-600 text-white" : "border-emerald-200 text-emerald-700"}`}
                        >
                          🎉 Paket Satıldı
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant={outcomeForm.type === "cancelled" ? "default" : "outline"}
                          onClick={() => setOutcomeForm({ ...outcomeForm, type: "cancelled" })}
                          className={`rounded-xl text-xs ${outcomeForm.type === "cancelled" ? "bg-red-600 text-white" : "border-red-200 text-red-600"}`}
                        >
                          ⚪ İlgilenmiyor
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant={outcomeForm.type === "callback" ? "default" : "outline"}
                          onClick={() => setOutcomeForm({ ...outcomeForm, type: "callback" })}
                          className={`rounded-xl text-xs ${outcomeForm.type === "callback" ? "bg-blue-600 text-white" : "border-blue-200 text-blue-700"}`}
                        >
                          🔄 Tekrar Aranacak
                        </Button>
                      </div>

                      {outcomeForm.type === "demo_scheduled" && (
                        <div className="space-y-2 pt-1 border-t">
                          <Label className="text-xs font-semibold">Demo Randevu Tarihi ve Saati *</Label>
                          <div className="grid grid-cols-2 gap-2">
                            <DatePickerTR
                              value={outcomeForm.scheduled_date ? outcomeForm.scheduled_date.split("T")[0] : ""}
                              onChange={(dateStr) => {
                                const currentHour = outcomeForm.scheduled_date?.split("T")[1] || "12:00";
                                setOutcomeForm({ ...outcomeForm, scheduled_date: `${dateStr}T${currentHour}` });
                              }}
                              returnDateString
                              placeholder="Gün.Ay.Yıl Seçin"
                            />
                            <TimePickerPopover
                              value={outcomeForm.scheduled_date?.split("T")[1]?.slice(0, 5) || "12:00"}
                              onChange={(timeStr) => {
                                const currentDate = outcomeForm.scheduled_date ? outcomeForm.scheduled_date.split("T")[0] : todayLocalString();
                                setOutcomeForm({ ...outcomeForm, scheduled_date: `${currentDate}T${timeStr}` });
                              }}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {callLogForm.result === "no_answer" && (selectedDemoLead.call_logs?.length || 0) === 2 && (
                    <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-xl text-xs space-y-1">
                      <p className="font-bold flex items-center gap-1.5">
                        <AlertCircle className="w-4 h-4" /> 3. arama da başarısız oldu!
                      </p>
                      <p>Bu kaydı onayladığınızda adaya ulaşılamadığı için süreç otomatik olarak 'Ulaşılamadı' statüsünde kapatılacaktır.</p>
                    </div>
                  )}

                  <div className="space-y-1">
                    <Label className="text-xs">Ek Not (İsteğe Bağlı)</Label>
                    <Textarea
                      placeholder="Görüşme detayları veya ekstra not ekleyebilirsiniz..."
                      value={callLogForm.note}
                      onChange={(e) => setCallLogForm({ ...callLogForm, note: e.target.value })}
                      className="rounded-xl text-xs"
                      rows={2}
                    />
                  </div>

                  <Button
                    disabled={isSubmittingLead}
                    onClick={async () => {
                      setIsSubmittingLead(true);
                      try {
                        let finalNote = callLogForm.note.trim();
                        if (!finalNote) {
                          if (callLogForm.result === "no_answer") {
                            finalNote = `${(selectedDemoLead.call_logs?.length || 0) + 1}. aramada ulaşılamadı.`;
                          } else if (outcomeForm.type === "callback") {
                            finalNote = "Görüşüldü, tekrar aranacak.";
                          } else if (outcomeForm.type === "demo_scheduled") {
                            finalNote = outcomeForm.scheduled_date
                              ? `Demo planlandı: ${format(new Date(outcomeForm.scheduled_date), "dd.MM.yyyy HH:mm")}`
                              : "Demo planlandı.";
                          } else if (outcomeForm.type === "converted") {
                            finalNote = "Paket satışı gerçekleşti.";
                          } else if (outcomeForm.type === "cancelled") {
                            finalNote = "Görüşüldü, ilgilenmiyor.";
                          }
                        }

                        await fetch(`${API}/demo-requests/${selectedDemoLead.demo_id}/call-log`, {
                          method: "POST",
                          headers: { "Content-Type": "application/json" },
                          credentials: "include",
                          body: JSON.stringify({
                            result: callLogForm.result,
                            note: finalNote
                          }),
                        });

                        if (callLogForm.result === "spoke" && outcomeForm.type && outcomeForm.type !== "callback") {
                          await fetch(`${API}/demo-requests/${selectedDemoLead.demo_id}/outcome`, {
                            method: "PUT",
                            headers: { "Content-Type": "application/json" },
                            credentials: "include",
                            body: JSON.stringify({
                              outcome_type: outcomeForm.type,
                              note: finalNote,
                              scheduled_date: outcomeForm.scheduled_date || null,
                            }),
                          });
                        }

                        toast.success("İşlem başarıyla kaydedildi.");
                        await loadDemoRequests();
                        window.dispatchEvent(new Event("demoStatusUpdated"));
                        setSelectedDemoLead(null);
                      } catch (err) {
                        toast.error(err.message || "Hata oluştu");
                      } finally {
                        setIsSubmittingLead(false);
                      }
                    }}
                    className="w-full bg-navy hover:bg-navy/90 text-white rounded-xl text-xs h-10 font-semibold"
                  >
                    {callLogForm.result === "spoke"
                      ? outcomeForm.type && outcomeForm.type !== "callback"
                        ? "Görüşmeyi ve Sonucu Kaydet (Talebi Kapat)"
                        : "Görüşme Notunu Kaydet (Tekrar Aranacak)"
                      : (selectedDemoLead.call_logs?.length || 0) === 2
                      ? "3. Aramayı Kaydet ve 'Ulaşılamadı' Olarak Kapat"
                      : `Ulaşılamadı Olarak Kaydet (${(selectedDemoLead.call_logs?.length || 0) + 1}. Arama)`}
                  </Button>
                </div>
              )}

              {selectedDemoLead.call_logs?.length > 0 && (
                <div className="space-y-2 pt-2 border-t">
                  <p className="text-xs font-semibold text-stone-500">Arama Geçmişi:</p>
                  {selectedDemoLead.call_logs.map((log, i) => (
                    <div key={i} className="text-xs bg-white p-2.5 rounded-xl border flex items-start justify-between">
                      <div>
                        <span className="font-bold text-navy">{log.attempt}. Arama:</span>{" "}
                        <span className={log.result === "spoke" ? "text-emerald-600 font-semibold" : "text-red-500 font-semibold"}>
                          {log.result === "spoke" ? "Görüşüldü" : "Ulaşılamadı"}
                        </span>
                        <p className="text-stone-600 mt-0.5">{log.note || "-"}</p>
                      </div>
                      <span className="text-[10px] text-stone-400">
                        {format(new Date(log.date), "dd.MM.yyyy HH:mm")}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              <div className="pt-2 border-t">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setSelectedDemoLead(null)}
                  className="w-full rounded-2xl h-11 border-stone-300 text-stone-700 hover:bg-slate-100 font-semibold text-sm"
                >
                  Pencereyi Kapat
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}